/**
 * src/pages/DbView.tsx
 *
 * DB Viewer — read-only Mongo inspection from the dashboard. Eight tabs:
 *
 *   Overview     — every viewable collection with total + last-24h count.
 *   Collections  — pick a collection, page through latest docs (JSON view).
 *   Scraped HTML — filter scraped_data by website / search term, click a
 *                  row to see the full doc (rendered as HTML if `data` is
 *                  HTML-shaped; pretty-printed JSON otherwise). Built for
 *                  blocking / bad-data investigation without leaving the
 *                  dashboard.
 *   Flow 24h     — per-source funnel: scraped → deals → emitted, with
 *                  conversion ratios. Fast way to spot silent drops in
 *                  the pipeline (eg "cex_uk: scraped 1.2k, deals 0").
 *   Lineage      — knowledge-graph trace by website OR unique_id. Shows
 *                  per-collection counts + sample IDs in the ingestion
 *                  path so we can chase a single deal back to its raw
 *                  scrape, or check why a website has 10k scrapes but
 *                  zero emissions.
 *   Trends       — 24h KPI cards (with deltas vs prior 24h), 4 sparkline
 *                  charts (scraped/deals/emitted/alerts), top sources,
 *                  scraper-status donut, alert-severity donut, and a
 *                  freshness list (stalest scrapers first).
 *   Pipeline     — deals-health breakdown per source: live/expired/
 *                  suspended/OOS stacked bars, quality-class split,
 *                  price stats, top categories.
 *   Heatmap      — alerts heatmap (top categories × hours) with
 *                  severity-tinted cells.
 *
 * All data is fetched via /v1/observe/dbview/* — never directly from
 * Mongo. Auth is the same x-api-key as the rest of the dashboard.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Activity, AlertTriangle, BarChart3, Database, Download, Filter, FileSearch, FlameKindling, GitBranch,
  HeartPulse, Layers, Loader2, Plus, RotateCcw, Save, SlidersHorizontal, Table2, TrendingDown,
  TrendingUp, Workflow, X as XIcon,
} from 'lucide-react';
import {
  getOverview,
  peekCollection,
  listScraped,
  getScrapedDoc,
  getScrapedDocSignals,
  getFlow24h,
  getLineage,
  getTrends,
  getDealsHealth,
  getAlertsHeatmap,
  getPipelineFunnel,
  getPipelineDecisions,
  getPipelineDropReasons,
  getPipelineSparkline,
  getSchema,
  runQuery,
  getAnchorConfig,
  patchAnchorConfig,
  getScoringRules,
  patchScoringRules,
  getImageCardMetrics,
  getEmitLatencyMetrics,
  getDeliveryLatencyMetrics,
  getBiExtract,
  downloadBiExtractFile,
  runAggregate,
  getSourceHealth,
  type SourceHealthRow,
  type SourceHealthStatus,
  type QualityConfigPayload,
  type CollectionStat,
  type LineageResp,
  type TimeSeriesPoint,
  type KpiDelta,
  type DealsHealthRow,
  type HeatmapRow,
  type FunnelStage,
  type DropReasonsResp,
  type FilterRow,
  type FilterOp,
  type BlockSignals,
  type AnchorFormulaConfig,
  type AnchorFormulaMode,
} from '@/api/dbview';
import { cn } from '@/lib/cn';
import { relativeTime, txt } from '@/lib/format';

type TabKey =
  | 'overview' | 'collections' | 'scraped' | 'flow' | 'lineage'
  | 'trends'   | 'pipeline'    | 'heatmap' | 'funnel' | 'query'
  | 'anchor_config' | 'scoring_rules' | 'bi_extract' | 'analyze' | 'source_health';

const TABS: Array<{ key: TabKey; label: string; icon: typeof Database }> = [
  { key: 'overview',      label: 'Overview',      icon: Database          },
  { key: 'trends',        label: 'Trends',        icon: Activity          },
  { key: 'funnel',        label: 'Pipeline',      icon: Workflow          },
  { key: 'pipeline',      label: 'Deals Health',  icon: FlameKindling     },
  { key: 'source_health', label: 'Source Health', icon: HeartPulse        },
  { key: 'heatmap',       label: 'Heatmap',       icon: AlertTriangle     },
  { key: 'collections',   label: 'Collections',   icon: Table2            },
  { key: 'query',         label: 'Query',         icon: Filter            },
  { key: 'analyze',       label: 'Analyze',       icon: BarChart3         },
  { key: 'bi_extract',    label: 'Data Extract',  icon: Download          },
  { key: 'scraped',       label: 'Scraped HTML',  icon: FileSearch        },
  { key: 'flow',          label: 'Flow 24h',      icon: Layers            },
  { key: 'lineage',       label: 'Lineage',       icon: GitBranch         },
  { key: 'anchor_config', label: 'Anchor Config', icon: SlidersHorizontal },
  { key: 'scoring_rules', label: 'Scoring Rules', icon: SlidersHorizontal },
];

export default function DbView() {
  const [tab, setTab] = useState<TabKey>('overview');

  return (
    <div className="space-y-4 max-w-7xl">
      <header className="flex items-start sm:items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold">DB View</h1>
          <p className="text-sm text-slate-500 mt-1">
            Read-only Mongo inspection · no SSH needed
          </p>
        </div>
      </header>

      <div className="flex items-center gap-1 bg-slate-900/60 border border-slate-800 rounded-md p-0.5 overflow-x-auto">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded transition shrink-0 whitespace-nowrap',
              tab === key ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white',
            )}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview'      && <OverviewTab />}
      {tab === 'trends'        && <TrendsTab />}
      {tab === 'funnel'        && <FunnelTab />}
      {tab === 'pipeline'      && <PipelineTab />}
      {tab === 'heatmap'       && <HeatmapTab />}
      {tab === 'collections'   && <CollectionsTab />}
      {tab === 'query'         && <QueryTab />}
      {tab === 'scraped'       && <ScrapedTab />}
      {tab === 'flow'          && <FlowTab />}
      {tab === 'lineage'       && <LineageTab />}
      {tab === 'anchor_config' && <AnchorConfigTab />}
      {tab === 'scoring_rules' && <ScoringRulesTab />}
      {tab === 'bi_extract'    && <BiExtractTab />}
      {tab === 'analyze'       && <AnalyzeTab />}
      {tab === 'source_health' && <SourceHealthTab />}
    </div>
  );
}

// ─── Analyze (Option C — safe read-only aggregations) ─────────────────────────

interface AggTemplate { label: string; collection: string; pipeline: unknown[]; days?: number }

const AGG_COLLECTIONS = ['pipeline_logs', 'deals', 'ebay_sold_listings'];

// IMPORTANT: pipeline_logs rows are EVALUATIONS, not deals — every in-stock
// deal is re-scored (and re-logged) each scrape cycle, so raw row counts
// overstate volume by 1.2×–680× depending on source. Every volume-shaped
// template below therefore counts DISTINCT unique_id via a two-stage $group
// (2026-07-03 metrics-honesty fix; Tony's 826× finding).
const AGG_TEMPLATES: AggTemplate[] = [
  {
    label: 'Drop reasons by source & category (distinct deals)',
    collection: 'pipeline_logs',
    days: 14,
    pipeline: [
      { $match: { decision: 'DROP' } },
      { $group: { _id: { source: '$source', category: '$category', reason: '$primary_reason', u: '$unique_id' } } },
      { $group: { _id: { source: '$_id.source', category: '$_id.category', reason: '$_id.reason' }, deals: { $sum: 1 } } },
      { $sort: { deals: -1 } },
      { $limit: 50 },
    ],
  },
  {
    label: 'Anchor-miss % by category, weekly (distinct deals)',
    collection: 'pipeline_logs',
    days: 28,
    pipeline: [
      { $group: {
        _id: { week: { $dateToString: { date: '$ts', format: '%Y-W%U' } }, category: '$category', u: '$unique_id' },
        anchor_miss: { $max: { $cond: [{ $eq: ['$market_validation_score', 5] }, 1, 0] } },
      } },
      { $group: {
        _id: { week: '$_id.week', category: '$_id.category' },
        deals: { $sum: 1 },
        anchor_miss: { $sum: '$anchor_miss' },
      } },
      { $sort: { '_id.week': -1, deals: -1 } },
    ],
  },
  {
    label: 'DCS distribution by band (distinct deals)',
    collection: 'pipeline_logs',
    days: 14,
    pipeline: [
      { $group: { _id: '$unique_id', dcs: { $max: '$dcs' } } },
      { $bucket: { groupBy: '$dcs', boundaries: [0, 60, 65, 70, 75, 85, 101], default: 'n/a', output: { deals: { $sum: 1 } } } },
    ],
  },
  {
    label: 'Emit vs Drop, week on week (distinct deals)',
    collection: 'pipeline_logs',
    days: 28,
    pipeline: [
      { $group: { _id: { week: { $dateToString: { date: '$ts', format: '%Y-W%U' } }, decision: '$decision', u: '$unique_id' } } },
      { $group: {
        _id: '$_id.week',
        emit: { $sum: { $cond: [{ $eq: ['$_id.decision', 'EMIT'] }, 1, 0] } },
        hold: { $sum: { $cond: [{ $eq: ['$_id.decision', 'HOLD'] }, 1, 0] } },
        drop: { $sum: { $cond: [{ $eq: ['$_id.decision', 'DROP'] }, 1, 0] } },
      } },
      { $sort: { _id: -1 } },
    ],
  },
  {
    label: 'Evaluations vs distinct deals by source (dup factor)',
    collection: 'pipeline_logs',
    days: 14,
    pipeline: [
      { $group: { _id: { source: '$source', u: '$unique_id' }, evals: { $sum: 1 } } },
      { $group: { _id: '$_id.source', deals: { $sum: 1 }, evaluations: { $sum: '$evals' } } },
      { $project: { deals: 1, evaluations: 1, dup_factor: { $round: [{ $divide: ['$evaluations', '$deals'] }, 1] } } },
      { $sort: { evaluations: -1 } },
    ],
  },
  {
    // Option B monitor — empty until fallback is deployed + flipped ON.
    label: 'Fallback emits by day (Option B monitor)',
    collection: 'pipeline_logs',
    days: 14,
    pipeline: [
      { $match: { emit_type: 'FALLBACK_ANCHOR_MISS' } },
      { $group: { _id: { day: { $dateToString: { date: '$ts', format: '%Y-%m-%d' } }, u: '$unique_id' } } },
      { $group: { _id: '$_id.day', deals: { $sum: 1 } } },
      { $sort: { _id: -1 } },
    ],
  },
];

function fmtCell(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/** Prepend a time window so templates scan an indexed slice (not all 11M+
 *  rows). {$date} is parsed back into a real Date server-side. Tony can widen
 *  the window or drop the stage in the editor. */
function windowed(pipeline: unknown[], days = 30): unknown[] {
  const since = new Date(Date.now() - days * 86_400_000).toISOString();
  return [{ $match: { ts: { $gte: { $date: since } } } }, ...pipeline];
}

function AnalyzeTab() {
  const [collection, setCollection] = useState('pipeline_logs');
  const [text, setText] = useState(() => JSON.stringify(windowed(AGG_TEMPLATES[0].pipeline, AGG_TEMPLATES[0].days), null, 2));

  const run = useMutation({
    mutationFn: async () => {
      let pipeline: unknown;
      try { pipeline = JSON.parse(text); } catch { throw new Error('Pipeline is not valid JSON'); }
      return runAggregate(collection, pipeline);
    },
  });

  function applyTemplate(t: AggTemplate) {
    setCollection(t.collection);
    setText(JSON.stringify(windowed(t.pipeline, t.days), null, 2));
    run.reset();
  }

  const rows = run.data?.rows ?? [];
  const cols = rows.length
    ? Array.from(rows.reduce((s, r) => { Object.keys(r).forEach(k => s.add(k)); return s; }, new Set<string>()))
    : [];
  const errMsg = run.isError
    ? ((run.error as { body?: { error?: string } })?.body?.error ?? (run.error as Error)?.message ?? 'query failed')
    : null;

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="bg-slate-900/60 border border-slate-800 rounded-md p-4">
        <h2 className="text-sm font-semibold text-slate-200">Analyze — read-only aggregations</h2>
        <p className="text-xs text-slate-500 mt-1">
          Live GROUP BY / distribution / trend queries over the analytics collections.
          Read-only &amp; capped — you can’t write or touch production. Pick a starter
          question, then tweak the pipeline.
        </p>
        <p className="text-xs text-amber-500/80 mt-1.5">
          ⚠️ pipeline_logs rows are <em>evaluations</em>, not deals — the same in-stock deal
          is re-logged every scrape cycle (1.2×–680× per source). The starter templates
          already count distinct deals; for your own queries, group by unique_id first.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {AGG_TEMPLATES.map(t => (
          <button key={t.label} onClick={() => applyTemplate(t)}
            className="px-3 py-1.5 text-xs rounded bg-slate-800 text-slate-200 hover:bg-slate-700 border border-slate-700">
            {t.label}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500">collection</label>
          <select value={collection} onChange={e => setCollection(e.target.value)}
            className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm text-slate-200">
            {AGG_COLLECTIONS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button onClick={() => run.mutate()} disabled={run.isPending}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded bg-emerald-700 text-white hover:bg-emerald-600 disabled:opacity-50">
            {run.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BarChart3 className="w-3.5 h-3.5" />}
            Run
          </button>
        </div>
        <textarea value={text} onChange={e => setText(e.target.value)} spellCheck={false} rows={12}
          className="w-full font-mono text-xs bg-slate-950 border border-slate-800 rounded p-3 text-slate-200" />
      </div>

      {errMsg && <ErrorRow label={errMsg} />}

      {run.data && (
        <div className="space-y-1">
          <div className="text-xs text-slate-500">{run.data.rowCount} rows{run.data.capped ? ' (capped)' : ''}</div>
          <div className="overflow-auto border border-slate-800 rounded max-h-[28rem]">
            <table className="text-xs w-full">
              <thead className="bg-slate-900 text-slate-400 sticky top-0">
                <tr>{cols.map(c => <th key={c} className="text-left font-medium px-2 py-1 whitespace-nowrap">{c}</th>)}</tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-slate-800/60">
                    {cols.map(c => (
                      <td key={c} className="px-2 py-1 font-mono text-slate-300 whitespace-nowrap">{fmtCell(r[c])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Data Extract (Option A) ─────────────────────────────────────────────────

const EXTRACT_LABELS: Record<string, string> = {
  'otr_data.sqlite.gz':       'SQLite — all 3 tables, indexed (for SQL practice)',
  'deals.csv.gz':             'deals.csv (Power BI)',
  'pipeline_logs.csv.gz':     'pipeline_logs.csv (Power BI)',
  'ebay_sold_listings.csv.gz': 'ebay_sold_listings.csv (Power BI)',
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${units[i]}`;
}

function BiExtractTab() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['dbview', 'bi-extract'],
    queryFn: getBiExtract,
    refetchInterval: 5 * 60_000,
  });
  const [downloading, setDownloading] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function onDownload(name: string) {
    setErr(null);
    setDownloading(name);
    try {
      await downloadBiExtractFile(name);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'download failed');
    } finally {
      setDownloading(null);
    }
  }

  if (isLoading) return <LoadingRow label="Loading data extract…" />;
  if (isError)   return <ErrorRow   label="Failed to load data extract." />;
  if (!data)     return null;

  const files = data.files.filter(f => f.name !== 'metadata.json');

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="bg-slate-900/60 border border-slate-800 rounded-md p-4">
        <h2 className="text-sm font-semibold text-slate-200">Day-fresh data extract</h2>
        <p className="text-xs text-slate-500 mt-1">
          Read-only copy of <span className="font-mono">deals</span>,{' '}
          <span className="font-mono">pipeline_logs</span> and{' '}
          <span className="font-mono">ebay_sold_listings</span> (last{' '}
          {data.window_days ?? 60} days) for SQL / Power BI. Rebuilt nightly —
          nothing here can touch production.
        </p>
        {data.built_at && (
          <p className="text-xs text-slate-400 mt-2">
            Last built <span className="text-slate-200">{relativeTime(data.built_at)}</span>
            {data.rows && (
              <> · {Object.entries(data.rows).map(([k, v]) => `${k} ${v.toLocaleString()}`).join(' · ')}</>
            )}
          </p>
        )}
      </div>

      {!data.available ? (
        <div className="bg-slate-900/60 border border-slate-800 rounded-md p-4 text-sm text-slate-400">
          No extract published yet — the nightly build hasn’t produced a file. Check back after the next run.
        </div>
      ) : (
        <div className="space-y-2">
          {files.map(f => (
            <div
              key={f.name}
              className="flex items-center justify-between gap-3 bg-slate-900/60 border border-slate-800 rounded-md p-3"
            >
              <div className="min-w-0">
                <div className="font-mono text-sm text-slate-200 truncate">{f.name}</div>
                <div className="text-xs text-slate-500">
                  {EXTRACT_LABELS[f.name] ?? ''} · {formatBytes(f.bytes)}
                </div>
              </div>
              <button
                onClick={() => onDownload(f.name)}
                disabled={downloading === f.name}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded transition shrink-0',
                  'bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50',
                )}
              >
                {downloading === f.name
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Download className="w-3.5 h-3.5" />}
                Download
              </button>
            </div>
          ))}
        </div>
      )}

      {err && <ErrorRow label={err} />}
    </div>
  );
}

// ─── Source Health (D6 — silent scraper/cleaner degradation watcher) ─────────

const SH_STYLE: Record<SourceHealthStatus, { dot: string; text: string; card: string; label: string }> = {
  critical: { dot: 'bg-red-500',    text: 'text-red-400',    card: 'border-red-900/60 bg-red-950/20',    label: 'CRITICAL' },
  warn:     { dot: 'bg-amber-500',  text: 'text-amber-400',  card: 'border-amber-900/60 bg-amber-950/20', label: 'WARN'    },
  ok:       { dot: 'bg-emerald-500', text: 'text-emerald-400', card: 'border-slate-800',                  label: 'OK'      },
};

function shPct(x: number): string { return `${Math.round(x * 100)}%`; }

/** One labelled metric chip; `bad` tints it amber when the value is off. */
function ShStat({ label, value, sub, bad }: { label: string; value: string; sub?: string; bad?: boolean }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={cn('text-sm font-mono', bad ? 'text-amber-400' : 'text-slate-200')}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 font-mono truncate">{sub}</div>}
    </div>
  );
}

function SourceHealthRowCard({ s }: { s: SourceHealthRow }) {
  const st = SH_STYLE[s.status];
  const m = s.metrics;
  const hasBaseline = m.evals_base_daily >= 20;
  return (
    <div className={cn('border rounded-md p-3', st.card)}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className={cn('w-2 h-2 rounded-full shrink-0', st.dot)} />
          <span className="font-mono text-sm text-slate-200">{s.source}</span>
          <span className={cn('text-[10px] font-semibold tracking-wider', st.text)}>{st.label}</span>
        </div>
        <div className="text-xs text-slate-400 font-mono">
          {m.evals_24h.toLocaleString()} evals/24h
        </div>
      </div>

      {s.status !== 'ok' && (
        <ul className="mt-2 space-y-0.5">
          {s.reasons.map((r, i) => (
            <li key={i} className={cn('text-xs', st.text)}>• {r}</li>
          ))}
        </ul>
      )}

      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <ShStat
          label="Volume (24h vs base)"
          value={hasBaseline ? shPct(m.yield_ratio) : '—'}
          sub={hasBaseline ? `~${Math.round(m.evals_base_daily).toLocaleString()}/day base` : 'below baseline floor'}
          bad={hasBaseline && m.yield_ratio < 0.4}
        />
        <ShStat
          label="modelKey rate"
          value={shPct(m.modelkey_pct_24h)}
          sub={`was ${shPct(m.modelkey_pct_base)}`}
          bad={hasBaseline && m.modelkey_pct_base >= 0.4 && (m.modelkey_pct_base - m.modelkey_pct_24h) >= 0.2}
        />
        <ShStat
          label="Unverified-cond drops"
          value={shPct(m.unverified_share_24h)}
          sub={`was ${shPct(m.unverified_share_base)}`}
          bad={(m.unverified_share_24h - m.unverified_share_base) >= 0.2 && m.unverified_share_24h >= 0.3}
        />
        <ShStat
          label="Stub-page rate"
          value={shPct(m.block_rate)}
          sub={m.block_rate >= 0.5 && m.yield_ratio >= 0.5 ? 'stubs, but volume ok' : 'recent scrapes'}
          bad={m.block_rate >= 0.5 && m.yield_ratio < 0.5}
        />
      </div>
    </div>
  );
}

function SourceHealthTab() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['dbview', 'source-health'],
    queryFn: getSourceHealth,
    refetchInterval: 2 * 60_000, // matches the 120s server cache
  });

  if (isLoading) return <LoadingRow label="Loading source health…" />;
  if (isError)   return <ErrorRow   label="Failed to load source health." />;
  if (!data)     return null;

  const counts = data.sources.reduce(
    (a, s) => { a[s.status]++; return a; },
    { critical: 0, warn: 0, ok: 0 } as Record<SourceHealthStatus, number>,
  );

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="bg-slate-900/60 border border-slate-800 rounded-md p-4">
        <h2 className="text-sm font-semibold text-slate-200">Source health</h2>
        <p className="text-xs text-slate-500 mt-1">
          Each scraper’s last 24h vs its trailing 7-day baseline — catches silent
          breaks where deals still flow but a field quietly drops (e.g. eBay’s
          <span className="font-mono"> su-*</span> markup change that lost{' '}
          <span className="font-mono">condition</span> on every listing), plus
          block/challenge pages and dead sources. The watcher also fires these to{' '}
          <span className="text-slate-300">Alerts</span> automatically every 30 min.
        </p>
        <div className="flex items-center gap-4 mt-3 text-xs">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500" />{counts.critical} critical</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-500" />{counts.warn} warn</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" />{counts.ok} ok</span>
          <span className="text-slate-500 ml-auto">updated {relativeTime(data.generatedAt)}</span>
        </div>
      </div>

      <div className="space-y-2">
        {data.sources.map(s => <SourceHealthRowCard key={s.source} s={s} />)}
      </div>

      <p className="text-[11px] text-slate-600">
        Baselines are the prior 7 days; sources under ~20 evals/day are skipped to
        avoid noise. “Unverified-cond drops” is the share of DROP decisions carrying{' '}
        <span className="font-mono">UNVERIFIED_CONDITION</span> — a jump means the
        condition selector likely broke. Read-only; this never gates the pipeline.
      </p>
    </div>
  );
}

// ─── Overview ────────────────────────────────────────────────────────────────

function OverviewTab() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['dbview', 'overview'],
    queryFn:  getOverview,
    refetchInterval: 5 * 60_000, // 5min — was 30s; reduced to take Mongo load off prod
  });

  if (isLoading) return <LoadingRow label="Loading collection counts…" />;
  if (isError)   return <ErrorRow   label="Failed to load overview." />;
  if (!data)     return null;

  // Group by `group` (scrapers / product / users / game / observe / admin).
  const byGroup = new Map<string, CollectionStat[]>();
  for (const s of data.stats) {
    if (!byGroup.has(s.group)) byGroup.set(s.group, []);
    byGroup.get(s.group)!.push(s);
  }

  return (
    <div className="space-y-5">
      {[...byGroup.entries()].map(([group, rows]) => (
        <section key={group}>
          <h2 className="text-xs font-semibold tracking-wider text-slate-500 uppercase mb-2">{group}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {rows.map(s => (
              <div
                key={s.name}
                className="bg-slate-900/60 border border-slate-800 rounded-md p-3 hover:border-slate-700 transition"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-mono text-sm text-slate-200">{s.name}</span>
                  <span className="text-xs text-slate-500">{group}</span>
                </div>
                <div className="mt-2 flex items-baseline justify-between gap-2">
                  <span className="text-2xl font-semibold tabular-nums text-slate-100">
                    {s.count.toLocaleString()}
                  </span>
                  {s.last_24h !== undefined && (
                    <span className="text-xs text-emerald-400 tabular-nums">
                      +{s.last_24h.toLocaleString()} / 24h
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

// ─── Collections (generic peek) ──────────────────────────────────────────────

function CollectionsTab() {
  const overview = useQuery({ queryKey: ['dbview', 'overview'], queryFn: getOverview });
  const allNames = (overview.data?.collections ?? []).map(c => c.name);
  const [name, setName] = useState<string>('scraped_data');
  const [filterField, setFilterField] = useState('');
  const [filterValue, setFilterValue] = useState('');

  const filter = filterField && filterValue
    ? { [filterField]: filterValue }
    : undefined;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['dbview', 'peek', name, filter],
    queryFn: () => peekCollection(name, { limit: 50, filter }),
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-slate-400 flex flex-col gap-1">
          collection
          <select
            value={name}
            onChange={e => setName(e.target.value)}
            className="bg-slate-900 border border-slate-800 rounded px-2 py-1.5 text-sm text-slate-100 min-w-[14rem]"
          >
            {allNames.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <label className="text-xs text-slate-400 flex flex-col gap-1">
          filter field
          <input
            value={filterField}
            onChange={e => setFilterField(e.target.value)}
            placeholder="(optional, eg website_name)"
            className="bg-slate-900 border border-slate-800 rounded px-2 py-1.5 text-sm w-44"
          />
        </label>
        <label className="text-xs text-slate-400 flex flex-col gap-1">
          value
          <input
            value={filterValue}
            onChange={e => setFilterValue(e.target.value)}
            placeholder="(eg cex_uk)"
            className="bg-slate-900 border border-slate-800 rounded px-2 py-1.5 text-sm w-44"
          />
        </label>
      </div>

      {isLoading && <LoadingRow label="Loading docs…" />}
      {isError   && <ErrorRow   label={`Query failed: ${(error as Error)?.message ?? 'unknown'}`} />}
      {data && (
        <>
          <div className="text-xs text-slate-500">
            {data.total.toLocaleString()} total · showing {data.docs.length}
          </div>
          <div className="space-y-1">
            {data.docs.map((d, i) => (
              <DocRow key={String((d as any)._id ?? i)} doc={d} />
            ))}
            {data.docs.length === 0 && (
              <div className="text-sm text-slate-500 py-4 text-center">No docs.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function DocRow({ doc }: { doc: Record<string, unknown> }) {
  const [open, setOpen] = useState(false);
  const summary = useMemo(() => {
    const id = String((doc as any)._id ?? '');
    const top = Object.entries(doc).slice(0, 3)
      .filter(([k]) => k !== '_id')
      .map(([k, v]) => {
        const s = typeof v === 'string' ? v : JSON.stringify(v);
        return `${k}=${s.slice(0, 40)}`;
      })
      .join('  ');
    return `${id}  ${top}`;
  }, [doc]);

  return (
    <div className="bg-slate-900/40 border border-slate-800 rounded">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full text-left px-3 py-2 text-xs font-mono text-slate-300 hover:bg-slate-900/80 truncate"
      >
        {summary}
      </button>
      {open && (
        <pre className="bg-slate-950 border-t border-slate-800 px-3 py-2 text-xs overflow-x-auto text-slate-300 whitespace-pre-wrap break-all">
          {JSON.stringify(doc, null, 2)}
        </pre>
      )}
    </div>
  );
}

// ─── Scraped HTML viewer ─────────────────────────────────────────────────────

function ScrapedTab() {
  const [website, setWebsite] = useState<string>('');
  const [search, setSearch]   = useState<string>('');
  const [skip,    setSkip]    = useState<number>(0);
  const [openId,  setOpenId]  = useState<string | null>(null);
  const PAGE = 100;

  // Reset pagination whenever a filter changes — otherwise switching
  // websites mid-page lands on a stale offset and shows "No matching
  // docs" even when there are matches on page 1.
  useEffect(() => { setSkip(0); }, [website, search]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['dbview', 'scraped', { website, search, skip }],
    queryFn: () => listScraped({
      website: website || undefined,
      search:  search  || undefined,
      limit:   PAGE,
      skip:    skip || undefined,
    }),
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1">
        <button
          onClick={() => setWebsite('')}
          className={cn(
            'px-2 py-1 text-xs rounded font-mono',
            website === '' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white bg-slate-900/60',
          )}
        >
          all
        </button>
        {(data?.websites ?? []).map(w => (
          <button
            key={w.website_name}
            onClick={() => setWebsite(w.website_name)}
            className={cn(
              'px-2 py-1 text-xs rounded font-mono',
              website === w.website_name
                ? 'bg-slate-800 text-white'
                : 'text-slate-400 hover:text-white bg-slate-900/60',
            )}
          >
            {w.website_name} <span className="text-slate-500">({w.count.toLocaleString()})</span>
          </button>
        ))}
      </div>

      <input
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Filter by search_term (exact match)"
        className="bg-slate-900 border border-slate-800 rounded px-2 py-1.5 text-sm w-full max-w-sm"
      />

      {isLoading && <LoadingRow label="Loading scraped docs…" />}
      {isError   && <ErrorRow   label="Failed to load scraped_data." />}
      {data && (
        <>
          <div className="text-xs text-slate-500">
            {data.total === null
              ? `showing ${data.docs.length} from offset ${data.skip.toLocaleString()}`
              : `${data.total.toLocaleString()} total · showing ${data.docs.length} from offset ${data.skip.toLocaleString()}`}
          </div>
          <div className="overflow-x-auto border border-slate-800 rounded">
            <table className="w-full text-xs">
              <thead className="bg-slate-900/80 text-slate-400">
                <tr>
                  <th className="text-left px-2 py-1.5 font-medium">when</th>
                  <th className="text-left px-2 py-1.5 font-medium">website</th>
                  <th className="text-left px-2 py-1.5 font-medium">search</th>
                  <th className="text-left px-2 py-1.5 font-medium">page</th>
                  <th className="text-left px-2 py-1.5 font-medium">processed</th>
                  <th className="text-left px-2 py-1.5 font-medium">data_hash</th>
                </tr>
              </thead>
              <tbody>
                {data.docs.map(d => (
                  <tr
                    key={d._id}
                    onClick={() => setOpenId(d._id)}
                    className="border-t border-slate-800 hover:bg-slate-900/50 cursor-pointer"
                  >
                    <td className="px-2 py-1.5 text-slate-400 whitespace-nowrap">
                      {d.timestamp ? relativeTime(d.timestamp) : '—'}
                    </td>
                    <td className="px-2 py-1.5 font-mono text-slate-200">{d.website_name ?? '—'}</td>
                    <td className="px-2 py-1.5 text-slate-300">{d.search_term ?? '—'}</td>
                    <td className="px-2 py-1.5 text-slate-400 tabular-nums">{d.page ?? '—'}</td>
                    <td className="px-2 py-1.5">
                      {d.processed
                        ? <span className="text-emerald-400">✓</span>
                        : <span className="text-amber-400">·</span>}
                    </td>
                    <td className="px-2 py-1.5 text-slate-500 font-mono truncate max-w-[12rem]">
                      {d.data_hash ?? '—'}
                    </td>
                  </tr>
                ))}
                {data.docs.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-2 py-4 text-center text-slate-500">
                      No matching docs.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination controls — cursor-style via skip + hasMore. We
              don't render a "page N of M" because we deliberately skip
              the count for search filters (the COLLSCAN was the original
              500). Prev/Next + the offset readout above is enough for
              an admin tool. */}
          <div className="flex items-center justify-end gap-2 text-xs">
            <button
              type="button"
              disabled={skip === 0}
              onClick={() => setSkip(s => Math.max(0, s - PAGE))}
              className={cn(
                'px-2 py-1 rounded border border-slate-800',
                skip === 0
                  ? 'text-slate-600 cursor-not-allowed'
                  : 'text-slate-300 hover:bg-slate-800',
              )}
            >
              ← prev
            </button>
            <button
              type="button"
              disabled={!data.hasMore}
              onClick={() => setSkip(s => s + PAGE)}
              className={cn(
                'px-2 py-1 rounded border border-slate-800',
                !data.hasMore
                  ? 'text-slate-600 cursor-not-allowed'
                  : 'text-slate-300 hover:bg-slate-800',
              )}
            >
              next →
            </button>
          </div>
        </>
      )}

      {openId && <ScrapedDocModal id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function ScrapedDocModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['dbview', 'scraped-doc', id],
    queryFn: () => getScrapedDoc(id),
  });
  const [view, setView] = useState<'rendered' | 'raw'>('raw');

  // The `data` field can be: a string of raw HTML, a JSON object of
  // already-parsed listings, or (rarely) a JSON object that itself has
  // an `.html` key. Try each in order.
  const raw = data?.doc.data;
  const htmlString = useMemo(() => {
    if (typeof raw === 'string' && /<html|<!doctype|<body/i.test(raw)) return raw;
    if (raw && typeof raw === 'object' && typeof (raw as any).html === 'string') {
      return (raw as any).html as string;
    }
    return null;
  }, [raw]);

  return (
    <div
      className="fixed inset-0 z-40 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-slate-900 border border-slate-800 rounded-lg max-w-5xl w-full max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <header className="px-4 py-3 border-b border-slate-800 flex items-center justify-between gap-3">
          <div className="text-sm font-mono text-slate-300 truncate">{id}</div>
          <div className="flex items-center gap-1">
            {htmlString && (
              <button
                onClick={() => setView(v => v === 'rendered' ? 'raw' : 'rendered')}
                className="px-2 py-1 text-xs rounded bg-slate-800 hover:bg-slate-700 text-white"
              >
                {view === 'rendered' ? 'view raw' : 'view rendered'}
              </button>
            )}
            <button
              onClick={onClose}
              className="px-2 py-1 text-xs rounded text-slate-400 hover:text-white"
            >
              close
            </button>
          </div>
        </header>
        <div className="px-4 pt-3">
          <BlockSignalsBadge id={id} />
        </div>
        <div className="flex-1 overflow-auto">
          {isLoading && <div className="p-4 text-sm text-slate-500">Loading…</div>}
          {isError   && <div className="p-4 text-sm text-rose-300">Failed to load doc.</div>}
          {data && (
            view === 'rendered' && htmlString
              ? (
                // Sandboxed iframe — never run scripts from a scraped page
                // inside our origin. `srcDoc` + sandbox without
                // allow-scripts gives us a visual peek without XSS risk.
                <iframe
                  title="scraped html preview"
                  sandbox=""
                  srcDoc={htmlString}
                  className="w-full h-[70vh] bg-white"
                />
              )
              : (
                <pre className="p-4 text-xs text-slate-300 whitespace-pre-wrap break-all">
                  {JSON.stringify(data.doc, null, 2)}
                </pre>
              )
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Flow 24h ────────────────────────────────────────────────────────────────

function FlowTab() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['dbview', 'flow24h'],
    queryFn: getFlow24h,
    refetchInterval: 5 * 60_000, // 5min — was 30s; reduced to take Mongo load off prod
  });

  // 2026-06-07 — emit→delivery latency alongside the flow volumes. Same
  // query key as the Scoring-Rules panel so React Query dedupes the fetch.
  const deliveryLatencyQ = useQuery({
    queryKey: ['dbview', 'delivery_latency'],
    queryFn:  () => getDeliveryLatencyMetrics(24),
    refetchInterval: 30_000,
  });

  if (isLoading) return <LoadingRow label="Computing 24h flow…" />;
  if (isError)   return <ErrorRow   label="Failed to load flow." />;
  if (!data)     return null;

  const t = data.totals;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-2">
        <Stat label="scraped"  value={t.scraped_24h} accent="text-sky-400"     />
        <Stat label="deals"    value={t.deals_24h}   accent="text-violet-400"  />
        <Stat label="emitted"  value={t.emitted_24h} accent="text-emerald-400" />
      </div>

      <div className="overflow-x-auto border border-slate-800 rounded">
        <table className="w-full text-xs">
          <thead className="bg-slate-900/80 text-slate-400">
            <tr>
              <th className="text-left px-2 py-1.5 font-medium">source</th>
              <th className="text-right px-2 py-1.5 font-medium">scraped</th>
              <th className="text-right px-2 py-1.5 font-medium">deals</th>
              <th className="text-right px-2 py-1.5 font-medium">emitted</th>
              <th className="text-right px-2 py-1.5 font-medium">norm %</th>
              <th className="text-right px-2 py-1.5 font-medium">emit %</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map(r => (
              <tr key={r.source} className="border-t border-slate-800">
                <td className="px-2 py-1.5 font-mono text-slate-200">{r.source}</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-sky-300">
                  {r.scraped_24h.toLocaleString()}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums text-violet-300">
                  {r.deals_24h.toLocaleString()}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums text-emerald-300">
                  {r.emitted_24h.toLocaleString()}
                </td>
                <td className={cn(
                  'px-2 py-1.5 text-right tabular-nums',
                  r.normalize_ratio === null ? 'text-slate-600' :
                  r.normalize_ratio === 0     ? 'text-rose-400' : 'text-slate-300',
                )}>
                  {r.normalize_ratio === null ? '—' : (r.normalize_ratio * 100).toFixed(1) + '%'}
                </td>
                <td className={cn(
                  'px-2 py-1.5 text-right tabular-nums',
                  r.emit_ratio === null ? 'text-slate-600' :
                  r.emit_ratio === 0     ? 'text-rose-400' : 'text-slate-300',
                )}>
                  {r.emit_ratio === null ? '—' : (r.emit_ratio * 100).toFixed(1) + '%'}
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && (
              <tr><td colSpan={6} className="px-2 py-4 text-center text-slate-500">No activity in last 24h.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500">
        scraped_data inserts vs deals inserts vs deliveries (emitted), grouped by source. Red norm/emit % flags a stuck pipeline.
      </p>

      {/* 2026-06-07 — emit→delivery latency, surfaced here next to the flow
          volumes so the "how fast do users get deals" question is answered
          on the same operational view. Also lives on the Scoring-Rules tab. */}
      <DeliveryLatencyPanel
        snapshot={deliveryLatencyQ.data?.snapshot}
        loading={deliveryLatencyQ.isLoading}
        error={deliveryLatencyQ.isError ? (deliveryLatencyQ.error as any)?.message : null}
      />
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent: string }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-md p-3">
      <div className="text-xs text-slate-500">{label} (24h)</div>
      <div className={cn('text-2xl font-semibold tabular-nums mt-1', accent)}>
        {value.toLocaleString()}
      </div>
    </div>
  );
}

// ─── Lineage / knowledge graph ───────────────────────────────────────────────

function LineageTab() {
  const [mode, setMode] = useState<'website' | 'unique_id'>('website');
  const [value, setValue] = useState('');
  const [submitted, setSubmitted] = useState<{ website?: string; unique_id?: string } | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['dbview', 'lineage', submitted],
    queryFn: () => getLineage(submitted!),
    enabled: !!submitted,
  });

  return (
    <div className="space-y-4">
      <form
        onSubmit={e => {
          e.preventDefault();
          if (!value.trim()) return;
          setSubmitted(mode === 'website' ? { website: value.trim() } : { unique_id: value.trim() });
        }}
        className="flex flex-wrap items-end gap-2"
      >
        <label className="text-xs text-slate-400 flex flex-col gap-1">
          trace by
          <select
            value={mode}
            onChange={e => setMode(e.target.value as any)}
            className="bg-slate-900 border border-slate-800 rounded px-2 py-1.5 text-sm"
          >
            <option value="website">website (eg cex_uk)</option>
            <option value="unique_id">unique_id (eg cex_uk:sps5…)</option>
          </select>
        </label>
        <label className="text-xs text-slate-400 flex flex-col gap-1 flex-1 min-w-[14rem]">
          value
          <input
            value={value}
            onChange={e => setValue(e.target.value)}
            placeholder={mode === 'website' ? 'cex_uk' : 'cex_uk:sps5playstation5'}
            className="bg-slate-900 border border-slate-800 rounded px-2 py-1.5 text-sm"
          />
        </label>
        <button
          type="submit"
          className="px-3 py-1.5 text-xs font-medium rounded bg-slate-800 hover:bg-slate-700 text-white"
        >
          trace
        </button>
      </form>

      {!submitted && (
        <p className="text-sm text-slate-500">
          Enter a website or a deal's unique_id to trace its path through scraped_data → deals → hot_deals → deliveries.
        </p>
      )}

      {submitted && isLoading && <LoadingRow label="Tracing…" />}
      {submitted && isError   && <ErrorRow   label={`Trace failed: ${(error as Error)?.message ?? 'unknown'}`} />}
      {data && <LineageGraph data={data} />}
    </div>
  );
}

function LineageGraph({ data }: { data: LineageResp }) {
  // Render a horizontal node-edge layout. We don't need a graph library
  // for a 4-node fixed pipeline — a flex row with arrow separators is
  // visually clearer and avoids a 100kB d3 dep.
  return (
    <div className="space-y-3">
      <div className="bg-slate-900/60 border border-slate-800 rounded-md p-3">
        <div className="text-xs text-slate-400">
          anchor: <span className="font-mono text-slate-200">{data.anchor.type}</span>
          {' = '}
          <span className="font-mono text-slate-200">{data.anchor.value}</span>
        </div>
      </div>

      <div className="flex flex-wrap items-stretch gap-2">
        {data.nodes.map((n, i) => (
          <Fragment key={n.collection}>
            <LineageNodeCard node={n} />
            {i < data.nodes.length - 1 && (
              <div className="flex items-center text-slate-600 px-1 self-center">
                <span className="hidden sm:block">→</span>
                <span className="sm:hidden">↓</span>
              </div>
            )}
          </Fragment>
        ))}
      </div>

      {data.edges.length > 0 && (
        <div className="text-xs text-slate-500 space-y-0.5">
          {data.edges.map(e => (
            <div key={`${e.from}-${e.to}`}>
              <span className="font-mono text-slate-400">{e.from}</span>
              {' → '}
              <span className="font-mono text-slate-400">{e.to}</span>
              {' '}via <span className="font-mono text-slate-300">{e.via}</span>
            </div>
          ))}
        </div>
      )}

      {data.notes.length > 0 && (
        <div className="bg-slate-900/40 border border-slate-800 rounded p-3 space-y-1">
          {data.notes.map((n, i) => (
            <div key={i} className="text-xs text-amber-300">• {n}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function LineageNodeCard({ node }: { node: { collection: string; count: number; sample_ids: string[] } }) {
  const empty = node.count === 0;
  return (
    <div className={cn(
      'bg-slate-900/60 border rounded-md p-3 min-w-[10rem] flex-1',
      empty ? 'border-rose-900/60' : 'border-slate-800',
    )}>
      <div className="text-xs text-slate-500 font-mono">{node.collection}</div>
      <div className={cn(
        'text-2xl font-semibold tabular-nums mt-1',
        empty ? 'text-rose-400' : 'text-slate-100',
      )}>
        {node.count.toLocaleString()}
      </div>
      {node.sample_ids.length > 0 && (
        <div className="mt-2 text-[10px] text-slate-500 font-mono space-y-0.5">
          {node.sample_ids.slice(0, 3).map(s => (
            <div key={s} className="truncate" title={s}>{s}</div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Shared UI bits ──────────────────────────────────────────────────────────

function LoadingRow({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-slate-500 py-3">
      <Loader2 className="w-4 h-4 animate-spin" />
      {label}
    </div>
  );
}

function ErrorRow({ label }: { label: string }) {
  return <div className="text-sm text-rose-300 py-3">{label}</div>;
}

// ─── Trends tab ──────────────────────────────────────────────────────────────

const SERIES_COLOR = {
  scraped: '#38bdf8', // sky-400
  deals:   '#a78bfa', // violet-400
  emitted: '#34d399', // emerald-400
  alerts:  '#fb7185', // rose-400
} as const;

function TrendsTab() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['dbview', 'trends'],
    queryFn:  getTrends,
    refetchInterval: 5 * 60_000, // 5min — was 30s; reduced to take Mongo load off prod
  });

  if (isLoading) return <LoadingRow label="Loading trends…" />;
  if (isError)   return <ErrorRow   label="Failed to load trends." />;
  if (!data)     return null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <KpiCard label="scraped" kpi={data.kpis.scraped} series={data.series.scraped} color={SERIES_COLOR.scraped} />
        <KpiCard label="deals"   kpi={data.kpis.deals}   series={data.series.deals}   color={SERIES_COLOR.deals}   />
        <KpiCard label="emitted" kpi={data.kpis.emitted} series={data.series.emitted} color={SERIES_COLOR.emitted} />
        <KpiCard label="alerts"  kpi={data.kpis.alerts}  series={data.series.alerts}  color={SERIES_COLOR.alerts}  invertGood />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card title="Top sources (scraped 24h)">
          <BarList
            items={data.top_sources.map(s => ({ label: s.source, value: s.n }))}
            color="#38bdf8"
          />
        </Card>

        <Card title="Scraper status (current)">
          <Donut
            items={data.scraper_status.map(s => ({
              label: s.status,
              value: s.n,
              color: STATUS_COLOR[s.status] ?? '#64748b',
            }))}
          />
        </Card>

        <Card title="Alerts by severity (24h)">
          <Donut
            items={data.alerts_by_severity.map(s => ({
              label: s.severity,
              value: s.n,
              color: SEVERITY_COLOR[s.severity] ?? '#64748b',
            }))}
          />
        </Card>

        <Card title="Freshness (stalest first)">
          <div className="space-y-1 text-xs">
            {data.freshness.map(f => (
              <div key={f.id} className="flex items-center justify-between gap-2">
                <span className="font-mono text-slate-200 truncate">{f.id}</span>
                <span className="flex items-center gap-2 shrink-0">
                  <span className={cn(
                    'px-1.5 py-0.5 rounded font-mono text-[10px]',
                    f.status === 'ok'      && 'bg-emerald-900/40 text-emerald-300',
                    f.status === 'blocked' && 'bg-amber-900/40   text-amber-300',
                    f.status === 'error'   && 'bg-rose-900/40    text-rose-300',
                    (f.status === 'never' || !f.status) && 'bg-slate-800 text-slate-400',
                  )}>{f.status ?? 'never'}</span>
                  <span className="text-slate-500 tabular-nums w-20 text-right">
                    {f.hours_since === null ? 'never' : `${f.hours_since}h ago`}
                  </span>
                </span>
              </div>
            ))}
            {data.freshness.length === 0 && (
              <div className="text-slate-500">No registered scrapers.</div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

const STATUS_COLOR: Record<string, string> = {
  ok:      '#34d399',
  blocked: '#fbbf24',
  error:   '#fb7185',
  never:   '#64748b',
  unknown: '#64748b',
};
const SEVERITY_COLOR: Record<string, string> = {
  info:     '#38bdf8',
  warn:     '#fbbf24',
  error:    '#fb923c',
  critical: '#f43f5e',
};

function KpiCard({
  label, kpi, series, color, invertGood,
}: {
  label: string;
  kpi: KpiDelta;
  series: TimeSeriesPoint[];
  color: string;
  invertGood?: boolean;
}) {
  const delta = kpi.delta_pct;
  const goodDir = delta === null ? 'flat'
    : (invertGood ? (delta < 0 ? 'good' : delta > 0 ? 'bad' : 'flat')
                  : (delta > 0 ? 'good' : delta < 0 ? 'bad' : 'flat'));
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-md p-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs text-slate-500">{label} (24h)</span>
        {delta !== null && (
          <span className={cn(
            'text-[10px] font-mono flex items-center gap-0.5',
            goodDir === 'good' && 'text-emerald-400',
            goodDir === 'bad'  && 'text-rose-400',
            goodDir === 'flat' && 'text-slate-500',
          )}>
            {delta >= 0
              ? <TrendingUp   className="w-3 h-3" />
              : <TrendingDown className="w-3 h-3" />}
            {delta >= 0 ? '+' : ''}{delta.toFixed(1)}%
          </span>
        )}
      </div>
      <div className="text-2xl font-semibold tabular-nums mt-1" style={{ color }}>
        {kpi.current_24h.toLocaleString()}
      </div>
      <Sparkline data={series} color={color} />
      <div className="text-[10px] text-slate-500 tabular-nums mt-1">
        prev 24h: {kpi.prev_24h.toLocaleString()}
      </div>
    </div>
  );
}

// ─── Pipeline Health tab ─────────────────────────────────────────────────────

function PipelineTab() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['dbview', 'deals-health'],
    queryFn:  getDealsHealth,
    refetchInterval: 5 * 60_000, // 5min — was 60s; reduced to take Mongo load off prod
  });
  const [sortBy, setSortBy] = useState<'total' | 'expired_pct'>('total');

  if (isLoading) return <LoadingRow label="Computing pipeline health…" />;
  if (isError)   return <ErrorRow   label="Failed to load deals health." />;
  if (!data)     return null;

  const t = data.totals;
  const liveTotalPct = t.total ? (t.live / t.total) * 100 : 0;

  const sortedRows = [...data.rows].sort((a, b) => {
    if (sortBy === 'total') return b.total - a.total;
    const aExp = a.total ? a.expired / a.total : 0;
    const bExp = b.total ? b.expired / b.total : 0;
    return bExp - aExp;
  });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
        <Stat label="total"     value={t.total}        accent="text-slate-100"   />
        <Stat label="live"      value={t.live}         accent="text-emerald-400" />
        <Stat label="expired"   value={t.expired}      accent="text-amber-400"   />
        <Stat label="suspended" value={t.suspended}    accent="text-rose-400"    />
        <Stat label="OOS"       value={t.out_of_stock} accent="text-orange-400"  />
      </div>

      <div className="bg-slate-900/60 border border-slate-800 rounded-md p-3">
        <div className="text-xs text-slate-500 mb-2">
          Liveness (across all sources): {liveTotalPct.toFixed(1)}% live
        </div>
        <StackedBar
          segments={[
            { value: t.live,         color: '#34d399', label: 'live'      },
            { value: t.expired,      color: '#fbbf24', label: 'expired'   },
            { value: t.suspended,    color: '#fb7185', label: 'suspended' },
            { value: t.out_of_stock, color: '#fb923c', label: 'OOS'       },
          ]}
        />
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-500">sort:</span>
        <button
          onClick={() => setSortBy('total')}
          className={cn(
            'px-2 py-1 text-xs rounded',
            sortBy === 'total' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white',
          )}
        >total</button>
        <button
          onClick={() => setSortBy('expired_pct')}
          className={cn(
            'px-2 py-1 text-xs rounded',
            sortBy === 'expired_pct' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white',
          )}
        >expired %</button>
      </div>

      <div className="overflow-x-auto border border-slate-800 rounded">
        <table className="w-full text-xs">
          <thead className="bg-slate-900/80 text-slate-400">
            <tr>
              <th className="text-left  px-2 py-1.5 font-medium">source</th>
              <th className="text-right px-2 py-1.5 font-medium">total</th>
              <th className="text-left  px-2 py-1.5 font-medium w-72">live / expired / susp / OOS</th>
              <th className="text-right px-2 py-1.5 font-medium">good / ok / bad</th>
              <th className="text-right px-2 py-1.5 font-medium">avg £</th>
              <th className="text-right px-2 py-1.5 font-medium">min</th>
              <th className="text-right px-2 py-1.5 font-medium">max</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map(r => <PipelineRow key={r.source} row={r} />)}
            {sortedRows.length === 0 && (
              <tr><td colSpan={7} className="px-2 py-4 text-center text-slate-500">No deals yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Card title="Top categories">
        <BarList
          items={data.top_categories.map(c => ({ label: c.category, value: c.n }))}
          color="#a78bfa"
        />
      </Card>
    </div>
  );
}

function PipelineRow({ row }: { row: DealsHealthRow }) {
  return (
    <tr className="border-t border-slate-800">
      <td className="px-2 py-1.5 font-mono text-slate-200">{row.source}</td>
      <td className="px-2 py-1.5 text-right tabular-nums text-slate-200">{row.total.toLocaleString()}</td>
      <td className="px-2 py-1.5">
        <StackedBar
          segments={[
            { value: row.live,         color: '#34d399', label: 'live'      },
            { value: row.expired,      color: '#fbbf24', label: 'expired'   },
            { value: row.suspended,    color: '#fb7185', label: 'suspended' },
            { value: row.out_of_stock, color: '#fb923c', label: 'OOS'       },
          ]}
          height={6}
        />
      </td>
      <td className="px-2 py-1.5 text-right tabular-nums text-slate-300 whitespace-nowrap">
        <span className="text-emerald-400">{row.good.toLocaleString()}</span> /
        <span className="text-slate-300"> {row.ok.toLocaleString()}</span> /
        <span className="text-rose-400"> {row.bad.toLocaleString()}</span>
      </td>
      <td className="px-2 py-1.5 text-right tabular-nums text-slate-300">
        {row.avg_price === null ? '—' : row.avg_price.toFixed(0)}
      </td>
      <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">
        {row.min_price === null ? '—' : row.min_price.toFixed(0)}
      </td>
      <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">
        {row.max_price === null ? '—' : row.max_price.toFixed(0)}
      </td>
    </tr>
  );
}

// ─── Heatmap tab ─────────────────────────────────────────────────────────────

function HeatmapTab() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['dbview', 'alerts-heatmap'],
    queryFn:  getAlertsHeatmap,
    refetchInterval: 5 * 60_000, // 5min — was 30s; reduced to take Mongo load off prod
  });

  if (isLoading) return <LoadingRow label="Building heatmap…" />;
  if (isError)   return <ErrorRow   label="Failed to load heatmap." />;
  if (!data)     return null;

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">
        Alerts in the last 24h, grouped by category × hour. Cell colour ramps from cool → hot
        based on count; the left dot marks the highest severity seen for that category.
      </p>

      <div className="overflow-x-auto border border-slate-800 rounded">
        <table className="text-xs border-collapse">
          <thead>
            <tr className="bg-slate-900/80 text-slate-400">
              <th className="text-left px-2 py-1.5 font-medium sticky left-0 bg-slate-900 z-10">category</th>
              {data.hours.map((h, i) => {
                const d = new Date(h);
                const hh = d.getUTCHours().toString().padStart(2, '0');
                return (
                  <th key={h} className="px-1 py-1.5 font-mono text-[10px] text-slate-500 text-center min-w-[18px]">
                    {i % 3 === 0 ? hh : ''}
                  </th>
                );
              })}
              <th className="px-2 py-1.5 text-right font-medium">total</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map(r => (
              <HeatmapTableRow key={r.category} row={r} max={data.max_cell} />
            ))}
            {data.rows.length === 0 && (
              <tr>
                <td colSpan={data.hours.length + 2} className="px-2 py-6 text-center text-slate-500">
                  No alerts in the last 24h.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-3 text-[10px] text-slate-500">
        <span>severity:</span>
        {(['info', 'warn', 'error', 'critical'] as const).map(s => (
          <span key={s} className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full" style={{ background: SEVERITY_COLOR[s] }} />
            {s}
          </span>
        ))}
        <span className="ml-3">density:</span>
        <span className="flex items-center gap-0.5">
          {[0.1, 0.3, 0.5, 0.7, 0.9].map(i => (
            <span key={i}
              className="w-3 h-3 rounded-sm"
              style={{ background: heatColor(i, 1) }}
            />
          ))}
        </span>
      </div>
    </div>
  );
}

function HeatmapTableRow({ row, max }: { row: HeatmapRow; max: number }) {
  return (
    <tr className="border-t border-slate-800">
      <td className="px-2 py-1 font-mono text-slate-200 sticky left-0 bg-slate-950/95 z-10">
        <span className="flex items-center gap-1.5">
          <span
            className="w-1.5 h-1.5 rounded-full shrink-0"
            style={{ background: row.max_severity ? SEVERITY_COLOR[row.max_severity] : '#64748b' }}
            title={row.max_severity ?? 'no severity'}
          />
          <span className="truncate max-w-[14rem]">{txt(row.category)}</span>
        </span>
      </td>
      {row.cells.map((n, i) => (
        <td
          key={i}
          className="text-center px-0 py-0 align-middle"
          style={{ background: n === 0 ? 'transparent' : heatColor(n, max) }}
          title={`${n} alerts`}
        >
          <div className="w-[18px] h-[18px] mx-auto" />
        </td>
      ))}
      <td className="px-2 py-1 text-right tabular-nums text-slate-300">{row.total.toLocaleString()}</td>
    </tr>
  );
}

/** Cool (slate) → hot (rose) ramp. Returns CSS rgba string. Exported for tests. */
export function heatColor(value: number, max: number): string {
  if (max <= 0 || value <= 0) return 'rgba(0,0,0,0)';
  const t = Math.min(1, value / max);
  const r = Math.round(30  + (244 - 30 ) * t);
  const g = Math.round(41  + (63  - 41 ) * t);
  const b = Math.round(59  + (94  - 59 ) * t);
  const a = 0.25 + 0.75 * t;
  return `rgba(${r},${g},${b},${a})`;
}

// ─── Tiny chart primitives ───────────────────────────────────────────────────

/**
 * Compute the [start, end) bucket-index ranges that fall inside Tony's
 * quiet-hours window (22:00–08:00 Europe/London). Returns an array of
 * fractional spans relative to the total bucket count, ready to be
 * scaled to SVG x coordinates. Returns [] when `hours` is not supplied.
 *
 * Mirrors `notifications.service.ts::isInQuietHours()` so the dashboard's
 * visual annotation matches the runtime gate.
 */
function quietHoursSpans(hours: string[] | undefined): Array<{ start: number; end: number }> {
  if (!hours || hours.length === 0) return [];
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', hour: 'numeric', hour12: false,
  });
  const inWindow = (iso: string) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return false;
    const h = parseInt(fmt.format(d), 10);
    return h >= 22 || h < 8;
  };

  const spans: Array<{ start: number; end: number }> = [];
  let runStart: number | null = null;
  for (let i = 0; i < hours.length; i++) {
    if (inWindow(hours[i])) {
      if (runStart === null) runStart = i;
    } else if (runStart !== null) {
      spans.push({ start: runStart, end: i });
      runStart = null;
    }
  }
  if (runStart !== null) spans.push({ start: runStart, end: hours.length });
  return spans;
}

function Sparkline({ data, color, height = 28, hours }: {
  data: TimeSeriesPoint[];
  color: string;
  height?: number;
  /** Optional ISO 8601 bucket timestamps. When provided, the SVG renders
   *  translucent vertical bands over buckets inside the 22:00–08:00 BST
   *  quiet-hours window so the overnight EMIT dip is annotated as
   *  expected behaviour rather than looking like a mystery drop. */
  hours?: string[];
}) {
  if (!data || data.length === 0) {
    return <div className="h-7 text-xs text-slate-600">no data</div>;
  }
  const max = Math.max(1, ...data.map(d => d.n));
  const w = 100;
  const stepX = w / Math.max(1, data.length - 1);
  const pts = data.map((d, i) => {
    const x = i * stepX;
    const y = height - (d.n / max) * (height - 2) - 1;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });
  const linePath = `M ${pts.join(' L ')}`;
  const areaPath = `${linePath} L ${w},${height} L 0,${height} Z`;
  const spans = quietHoursSpans(hours);
  return (
    <svg
      viewBox={`0 0 ${w} ${height}`}
      preserveAspectRatio="none"
      className="w-full mt-2"
      style={{ height }}
    >
      {/* Quiet-hours bands sit BEHIND the line so the data stays prominent. */}
      {spans.map((s, i) => {
        const x  = s.start * stepX;
        const wd = (s.end - s.start) * stepX;
        return (
          <rect
            key={`qh-${i}`}
            x={x} y={0} width={wd} height={height}
            fill="#6366f1" fillOpacity={0.10}
          >
            <title>quiet hours (22:00–08:00 BST)</title>
          </rect>
        );
      })}
      <path d={areaPath} fill={color} fillOpacity={0.15} />
      <path d={linePath} fill="none" stroke={color} strokeWidth={1} />
    </svg>
  );
}

function StackedBar({ segments, height = 8 }: {
  segments: Array<{ value: number; color: string; label: string }>;
  height?: number;
}) {
  const total = segments.reduce((a, s) => a + s.value, 0);
  if (total === 0) return <div className="text-xs text-slate-600">no data</div>;
  return (
    <div className="flex w-full overflow-hidden rounded" style={{ height }}>
      {segments.map((s, i) => (
        <div
          key={i}
          style={{ width: `${(s.value / total) * 100}%`, background: s.color }}
          title={`${s.label}: ${s.value.toLocaleString()} (${((s.value / total) * 100).toFixed(1)}%)`}
        />
      ))}
    </div>
  );
}

function BarList({ items, color }: { items: Array<{ label: string; value: number }>; color: string }) {
  if (items.length === 0) {
    return <div className="text-xs text-slate-500">No data.</div>;
  }
  const max = Math.max(...items.map(i => i.value), 1);
  return (
    <div className="space-y-1">
      {items.map(i => (
        <div key={i.label} className="flex items-center gap-2 text-xs">
          <span className="font-mono text-slate-300 truncate w-32 shrink-0">{i.label}</span>
          <div className="flex-1 bg-slate-900 rounded h-2 overflow-hidden">
            <div
              className="h-full rounded"
              style={{ width: `${(i.value / max) * 100}%`, background: color }}
            />
          </div>
          <span className="tabular-nums text-slate-400 w-16 text-right">{i.value.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

function Donut({ items }: { items: Array<{ label: string; value: number; color: string }> }) {
  const total = items.reduce((a, i) => a + i.value, 0);
  if (total === 0) {
    return <div className="text-xs text-slate-500">No data.</div>;
  }
  let acc = 0;
  const stops = items.map(i => {
    const start = (acc / total) * 360;
    acc += i.value;
    const end   = (acc / total) * 360;
    return `${i.color} ${start}deg ${end}deg`;
  }).join(', ');
  return (
    <div className="flex items-center gap-4">
      <div
        className="w-24 h-24 rounded-full shrink-0"
        style={{ background: `conic-gradient(${stops})` }}
      >
        <div className="w-full h-full flex items-center justify-center">
          <div className="bg-slate-900 rounded-full w-14 h-14 flex items-center justify-center text-xs tabular-nums text-slate-300">
            {total.toLocaleString()}
          </div>
        </div>
      </div>
      <div className="flex-1 space-y-1 text-xs">
        {items.map(i => (
          <div key={i.label} className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-sm" style={{ background: i.color }} />
            <span className="font-mono text-slate-300 flex-1">{i.label}</span>
            <span className="tabular-nums text-slate-400">
              {i.value.toLocaleString()}{' '}
              <span className="text-slate-600">({((i.value / total) * 100).toFixed(0)}%)</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-md p-3">
      <h3 className="text-xs font-semibold tracking-wider text-slate-400 uppercase mb-2">{title}</h3>
      {children}
    </div>
  );
}

// ─── Funnel tab (Tab 1 in plan: pipeline funnel + decisions + drop-reasons) ─

const PIPELINE_DECISION_COLOR: Record<string, string> = {
  EMIT:     '#34d399',
  HOLD:     '#fbbf24',
  DROP:     '#fb7185',
  SUPPRESS: '#94a3b8',
  unknown:  '#64748b',
};

/** Slider stops for the "Decisions by category × source" time window.
 *  Discrete instead of continuous so labels stay clean and we get free
 *  React-Query cache reuse when the user revisits a stop.
 *
 *  2026-05-08 (Sumit): dropped 14d + 30d. Even with the
 *  `ts_source_decision_category` covering index, 14d → ~2.8s and 30d →
 *  ~7.7s on prod's 6.4M-row pipeline_logs collection. With the dashboard
 *  refetching every 5 min while a page is open, that's significant load
 *  on the same Mongo that's already flapping its health probe. 7d is
 *  enough for trend analysis; one-off deeper digs go via /db-view/query
 *  or a Mongo shell. The endpoint itself still ACCEPTS up to 720h
 *  (clamp + tiered maxTimeMS in the repo) so a power user can still pass
 *  ?windowHours=720 manually if needed. */
const WINDOW_PRESETS: Array<{ hours: number; label: string }> = [
  { hours: 0.5,    label: '30m' },
  { hours: 1,      label: '1h'  },
  { hours: 2,      label: '2h'  },
  { hours: 4,      label: '4h'  },
  { hours: 8,      label: '8h'  },
  { hours: 12,     label: '12h' },
  { hours: 24,     label: '24h' },
  { hours: 48,     label: '2d'  },
  { hours: 72,     label: '3d'  },
  { hours: 24 * 7, label: '7d'  },
];

function FunnelTab() {
  const funnel = useQuery({
    queryKey: ['dbview', 'funnel', 'byDealTier'],
    // 2026-05-10 — Tony calibration #4. Opt-in flag splits EMIT into
    // Exceptional / Strong / Standard via a $lookup on `deals.discountPct`.
    // The chart renders three sub-segments inside the EMIT bar.
    queryFn:  () => getPipelineFunnel({ byDealTier: true }),
    refetchInterval: 5 * 60_000, // 5min — was 30s; reduced to take Mongo load off prod
  });
  const [scope, setScope] = useState<'today' | 'all_active'>('today');
  const decisions = useQuery({
    queryKey: ['dbview', 'decisions', scope],
    queryFn:  () => getPipelineDecisions({ scope }),
    refetchInterval: 5 * 60_000, // 5min — was 30s; reduced to take Mongo load off prod
  });

  // Time window for the category-pivot card. Discrete preset stops mapped
  // to a slider index — log-ish scale from 30min to 30 days. Default = 24h.
  const [windowIdx, setWindowIdx] = useState(WINDOW_PRESETS.findIndex(p => p.hours === 24));
  const windowHours = WINDOW_PRESETS[windowIdx]?.hours ?? 24;
  // Windows over 24h are too expensive to auto-refresh on a 5-min cadence
  // — even with the covering index, multi-day aggregations against
  // pipeline_logs take 1–8s. Manual refresh only beyond 24h; user
  // re-clicks the window or refreshes the page if they want fresh data.
  const refetchMs = windowHours <= 24 ? 5 * 60_000 : false as const;
  const decisionsByCategory = useQuery({
    // The category-pivot view is inherently a time-windowed read, so it
    // always uses 'today' scope (irrespective of the table-above toggle).
    queryKey: ['dbview', 'decisions', 'today', windowHours, 'by-category'],
    queryFn:  () => getPipelineDecisions({ scope: 'today', groupByCategory: true, windowHours }),
    refetchInterval: refetchMs,
  });
  const reasons = useQuery({
    queryKey: ['dbview', 'drop-reasons'],
    queryFn:  () => getPipelineDropReasons({}),
    refetchInterval: 5 * 60_000, // 5min — was 30s; reduced to take Mongo load off prod
  });
  const sparkline = useQuery({
    queryKey: ['dbview', 'spark'],
    queryFn:  () => getPipelineSparkline({ bucketHours: 1, buckets: 48 }),
    refetchInterval: 5 * 60_000, // 5min — was 30s; reduced to take Mongo load off prod
  });

  return (
    <div className="space-y-4">
      {/* Funnel */}
      <Card title="Funnel — last 24h (scraped → parsed → deals → EMIT/HOLD/DROP → delivered)">
        {funnel.isLoading && <LoadingRow label="Computing funnel…" />}
        {funnel.isError   && <ErrorRow   label="Failed to load funnel." />}
        {funnel.data && <FunnelChart stages={funnel.data.stages} />}
      </Card>

      {/* Decisions crosstab */}
      <div className="bg-slate-900/60 border border-slate-800 rounded-md p-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold tracking-wider text-slate-400 uppercase">
            Decisions by source
          </h3>
          <div className="flex items-center gap-1 bg-slate-950/60 border border-slate-800 rounded p-0.5">
            {(['today', 'all_active'] as const).map(s => (
              <button
                key={s}
                onClick={() => setScope(s)}
                className={cn(
                  'px-2 py-0.5 text-xs rounded',
                  scope === s ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white',
                )}
              >
                {s === 'today' ? 'today' : 'all-active'}
              </button>
            ))}
          </div>
        </div>
        {decisions.isLoading && <LoadingRow label="Loading decisions…" />}
        {decisions.data && <DecisionsTable cells={decisions.data.cells} />}
      </div>

      {/* Decisions by category × source — windowed by slider, independent
          of the today/all-active toggle on the card above. */}
      <div className="bg-slate-900/60 border border-slate-800 rounded-md p-3 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold tracking-wider text-slate-400 uppercase">
            Decisions by category × source
          </h3>
          <span className="text-[11px] tabular-nums text-slate-300">
            window: <span className="font-mono text-white">{WINDOW_PRESETS[windowIdx].label}</span>
          </span>
        </div>
        <WindowSlider value={windowIdx} onChange={setWindowIdx} />
        {windowHours > 24 && (
          <div className="text-[11px] text-amber-400/80 leading-relaxed">
            ⚠ Wider windows aggregate against ~6 M+ pipeline_logs rows.
            Auto-refresh is disabled for &gt; 24h to keep the load off Mongo —
            re-click the window to refresh.
          </div>
        )}
        {decisionsByCategory.isLoading && <LoadingRow label="Loading category breakdown…" />}
        {decisionsByCategory.data && <DecisionsByCategory cells={decisionsByCategory.data.cells} />}
      </div>

      {/* DROP-reason heatmap */}
      <Card title="DROP reasons × source (last 24h)">
        {reasons.isLoading && <LoadingRow label="Computing DROP reasons…" />}
        {reasons.data && <DropReasonsHeatmap data={reasons.data} />}
      </Card>

      {/* Hourly EMIT/DROP sparklines — quiet-hours bands (22:00–08:00 BST)
          overlaid via the `hours` prop so admins see the overnight EMIT
          dip annotated as expected, not as a mystery drop. */}
      <Card title="Hourly EMIT / DROP / HOLD (last 48h) · indigo bands = quiet hours">
        {sparkline.isLoading && <LoadingRow label="Computing sparklines…" />}
        {sparkline.data && (
          <div className="space-y-2">
            <SparklineRow label="EMIT" data={sparkline.data.emit.map(n => ({ hour: '', n }))} color={PIPELINE_DECISION_COLOR.EMIT} hours={sparkline.data.hours} />
            <SparklineRow label="HOLD" data={sparkline.data.hold.map(n => ({ hour: '', n }))} color={PIPELINE_DECISION_COLOR.HOLD} hours={sparkline.data.hours} />
            <SparklineRow label="DROP" data={sparkline.data.drop.map(n => ({ hour: '', n }))} color={PIPELINE_DECISION_COLOR.DROP} hours={sparkline.data.hours} />
          </div>
        )}
      </Card>
    </div>
  );
}

// 2026-05-10 — dealTier sub-bar palette for the EMIT-by-tier breakdown.
// Brighter→dimmer matches the "intensity" of the band (Exceptional bright,
// Standard muted). Mirrors the emoji semantics in deal cards.
const DEAL_TIER_BAR_COLOR = {
  exceptional: '#fb7185', // rose-400 — 🔥
  strong:      '#34d399', // emerald-400 — ✓
  standard:    '#64748b', // slate-500 — •
} as const;

function FunnelChart({ stages }: { stages: FunnelStage[] }) {
  const max = Math.max(...stages.map(s => s.count), 1);
  return (
    <div className="space-y-1">
      {stages.map(s => {
        const widthPct = (s.count / max) * 100;
        const bg = PIPELINE_DECISION_COLOR[s.name] ?? '#38bdf8';
        // EMIT-by-dealTier breakdown — render three stacked sub-segments
        // inside the EMIT bar when the optional `by_deal_tier` field is
        // populated. Falls back to a single-color bar otherwise.
        const subSegments = s.name === 'EMIT' && s.by_deal_tier
          ? (() => {
              const t = s.by_deal_tier;
              const sum = t.exceptional + t.strong + t.standard;
              if (sum <= 0) return null;
              return [
                { key: 'exceptional', n: t.exceptional, color: DEAL_TIER_BAR_COLOR.exceptional, emoji: '🔥' },
                { key: 'strong',      n: t.strong,      color: DEAL_TIER_BAR_COLOR.strong,      emoji: '✓' },
                { key: 'standard',    n: t.standard,    color: DEAL_TIER_BAR_COLOR.standard,    emoji: '•' },
              ];
            })()
          : null;
        return (
          <div key={s.name} className="flex items-center gap-2 text-xs">
            <span className="font-mono w-20 text-slate-300">{s.name}</span>
            <div className="flex-1 bg-slate-900 rounded h-5 overflow-hidden">
              {subSegments ? (
                <div
                  className="h-full rounded flex overflow-hidden"
                  style={{ width: `${widthPct}%`, minWidth: s.count ? '2.5rem' : '0' }}
                  title={`EMIT split by dealTier — ${s.count.toLocaleString()} total`}
                >
                  {subSegments.map(seg => (
                    <div
                      key={seg.key}
                      className="h-full flex items-center justify-center text-[10px] tabular-nums text-slate-900"
                      style={{
                        flex: `${seg.n} 0 0`,
                        background: seg.color,
                        minWidth: seg.n ? '1.5rem' : '0',
                      }}
                      title={`${seg.emoji} ${seg.key}: ${seg.n.toLocaleString()}`}
                    >
                      {seg.n > 0 ? `${seg.emoji} ${seg.n.toLocaleString()}` : ''}
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  className="h-full rounded flex items-center px-2 text-[10px] tabular-nums text-slate-900"
                  style={{ width: `${widthPct}%`, background: bg, minWidth: s.count ? '2.5rem' : '0' }}
                >
                  {s.count.toLocaleString()}
                </div>
              )}
            </div>
            <span className="tabular-nums text-slate-500 w-16 text-right">
              {s.drop_off_pct === null ? '—' : `−${s.drop_off_pct.toFixed(1)}%`}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function DecisionsTable({ cells }: { cells: Array<{ source: string; decision: string; n: number }> }) {
  // Pivot cells into a (source × decision) grid.
  const sources = [...new Set(cells.map(c => c.source))].sort();
  const decisions = [...new Set(cells.map(c => c.decision))].sort();
  const grid = new Map<string, number>();
  for (const c of cells) grid.set(`${c.source}|${c.decision}`, c.n);

  if (sources.length === 0) {
    return <div className="text-xs text-slate-500">No decisions in this scope.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="text-xs">
        <thead className="text-slate-400">
          <tr>
            <th className="text-left px-2 py-1 font-medium">source</th>
            {decisions.map(d => (
              <th key={d} className="text-right px-2 py-1 font-mono"
                style={{ color: PIPELINE_DECISION_COLOR[d] ?? '#cbd5e1' }}>
                {d}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sources.map(s => (
            <tr key={s} className="border-t border-slate-800">
              <td className="px-2 py-1 font-mono text-slate-200">{s}</td>
              {decisions.map(d => (
                <td key={d} className="px-2 py-1 text-right tabular-nums text-slate-300">
                  {(grid.get(`${s}|${d}`) ?? 0).toLocaleString()}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Discrete-stop range slider over WINDOW_PRESETS. Native <input type=range>
 * is index-based; we render tick labels under the track so each stop is
 * legible at a glance without a custom component.
 */
function WindowSlider({ value, onChange }: { value: number; onChange: (idx: number) => void }) {
  return (
    <div>
      <input
        type="range"
        min={0}
        max={WINDOW_PRESETS.length - 1}
        step={1}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="w-full accent-emerald-400 cursor-pointer"
        aria-label="time window"
      />
      <div className="flex justify-between mt-1 text-[10px] font-mono text-slate-500 select-none">
        {WINDOW_PRESETS.map((p, i) => (
          <span
            key={p.hours}
            className={cn('flex-1 text-center', i === value && 'text-emerald-300')}
          >
            {p.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * Renders one mini-table per category, each showing the same source × decision
 * pivot the existing `DecisionsTable` produces. Categories are sorted by
 * total volume (largest first) so the busy ones surface at the top.
 */
function DecisionsByCategory({
  cells,
}: {
  cells: Array<{ source: string; decision: string; n: number; category?: string }>;
}) {
  if (cells.length === 0) {
    return <div className="text-xs text-slate-500">No decisions in this scope.</div>;
  }

  // Bucket cells by category, summing per-category totals for sort.
  const byCategory = new Map<string, { total: number; rows: typeof cells }>();
  for (const c of cells) {
    const cat = c.category ?? 'uncategorized';
    let bucket = byCategory.get(cat);
    if (!bucket) {
      bucket = { total: 0, rows: [] };
      byCategory.set(cat, bucket);
    }
    bucket.total += c.n;
    bucket.rows.push(c);
  }

  const sortedCategories = [...byCategory.entries()].sort((a, b) => b[1].total - a[1].total);

  return (
    <div className="space-y-3">
      {sortedCategories.map(([category, { total, rows }]) => (
        <div key={category} className="border border-slate-800 rounded-md overflow-hidden">
          <div className="flex items-center justify-between bg-slate-950/50 px-3 py-1.5 border-b border-slate-800">
            <span className="text-xs font-mono text-slate-200">{category}</span>
            <span className="text-[10px] tabular-nums text-slate-500">
              {total.toLocaleString()} total
            </span>
          </div>
          <div className="px-3 py-2">
            <DecisionsTable cells={rows} />
          </div>
        </div>
      ))}
    </div>
  );
}

function DropReasonsHeatmap({ data }: { data: DropReasonsResp }) {
  if (data.rows.length === 0) {
    return <div className="text-xs text-slate-500">No DROPs in last 24h.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="text-xs border-collapse">
        <thead className="text-slate-400">
          <tr>
            <th className="text-left px-2 py-1 font-medium sticky left-0 bg-slate-900 z-10">reason</th>
            {data.sources.map(s => (
              <th key={s} className="px-2 py-1 font-mono text-[10px] text-center min-w-[5rem]">{s}</th>
            ))}
            <th className="px-2 py-1 text-right">total</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map(r => (
            <tr key={r.reason} className="border-t border-slate-800">
              <td className="px-2 py-1 font-mono text-slate-200 sticky left-0 bg-slate-950/95 z-10 truncate max-w-[20rem]">
                {r.reason}
              </td>
              {r.cells.map((n, i) => (
                <td
                  key={i}
                  className="text-center px-1 py-1 tabular-nums text-slate-200"
                  style={{ background: n === 0 ? 'transparent' : heatColor(n, data.max_cell) }}
                  title={`${n} drops`}
                >
                  {n > 0 ? n.toLocaleString() : ''}
                </td>
              ))}
              <td className="px-2 py-1 text-right tabular-nums text-slate-300">{r.total.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SparklineRow({ label, data, color, hours }: {
  label: string;
  data: TimeSeriesPoint[];
  color: string;
  /** Forwarded to Sparkline for the quiet-hours band overlay. */
  hours?: string[];
}) {
  const total = data.reduce((a, p) => a + p.n, 0);
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="font-mono w-12" style={{ color }}>{label}</span>
      <div className="flex-1">
        <Sparkline data={data} color={color} hours={hours} />
      </div>
      <span className="tabular-nums text-slate-400 w-16 text-right">
        {total.toLocaleString()}
      </span>
    </div>
  );
}

// ─── Query tab (Tab 6 power-features: filter builder + schema picker) ───────

const FILTER_OPS: FilterOp[] = ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'contains', 'exists', 'in'];

function QueryTab() {
  const overview = useQuery({ queryKey: ['dbview', 'overview'], queryFn: getOverview });
  const allNames = (overview.data?.collections ?? []).map(c => c.name);

  const [name, setName] = useState<string>('deals');
  const [rows, setRows] = useState<FilterRow[]>([]);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState<{ name: string; rows: FilterRow[]; projection: string[] } | null>(null);

  const schema = useQuery({
    queryKey: ['dbview', 'schema', name],
    queryFn:  () => getSchema(name),
    enabled:  !!name,
  });

  const result = useQuery({
    queryKey: ['dbview', 'query', submitted],
    queryFn:  () => runQuery({
      name:        submitted!.name,
      filterRows:  submitted!.rows,
      projection:  submitted!.projection.length ? submitted!.projection : undefined,
      limit:       100,
    }),
    enabled: !!submitted,
  });

  const csvHref = submitted
    ? `/v1/observe/dbview/query/csv` // Note: requires POST — UI uses the Download button below.
    : null;

  function downloadCsv() {
    if (!submitted) return;
    // We can't link <a download> to a POST endpoint; do a fetch + blob.
    const TOKEN_KEY = 'otr.observe.api_key';
    const apiKey = localStorage.getItem(TOKEN_KEY) ?? '';
    fetch('/v1/observe/dbview/query/csv', {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-api-key': apiKey },
      body: JSON.stringify({
        name:       submitted.name,
        filterRows: submitted.rows,
        projection: submitted.projection.length ? submitted.projection : undefined,
        limit:      500,
      }),
    }).then(r => r.blob()).then(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${submitted.name}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }).catch(() => { /* surfaced via the result query if it fails */ });
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-slate-400 flex flex-col gap-1">
          collection
          <select
            value={name}
            onChange={e => { setName(e.target.value); setRows([]); setSelectedFields([]); setSubmitted(null); }}
            className="bg-slate-900 border border-slate-800 rounded px-2 py-1.5 text-sm text-slate-100 min-w-[14rem]"
          >
            {allNames.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <button
          onClick={() => setRows([...rows, { field: '', op: 'eq', value: '' }])}
          className="px-2.5 py-1.5 text-xs rounded bg-slate-800 hover:bg-slate-700 text-white flex items-center gap-1"
        >
          <Plus className="w-3.5 h-3.5" /> add filter
        </button>
        <button
          onClick={() => setSubmitted({ name, rows: rows.filter(r => r.field), projection: selectedFields })}
          disabled={!name}
          className="px-3 py-1.5 text-xs rounded bg-emerald-700 hover:bg-emerald-600 disabled:bg-slate-800 text-white"
        >
          run query
        </button>
        {csvHref && submitted && (
          <button
            onClick={downloadCsv}
            className="px-2.5 py-1.5 text-xs rounded bg-slate-800 hover:bg-slate-700 text-white"
          >
            download CSV
          </button>
        )}
      </div>

      {/* Filter rows */}
      <div className="space-y-1">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <input
              value={r.field}
              onChange={e => updateRow(setRows, rows, i, { ...r, field: e.target.value })}
              placeholder="field path"
              list={`schema-${name}`}
              className="bg-slate-900 border border-slate-800 rounded px-2 py-1.5 w-44 font-mono"
            />
            <select
              value={r.op}
              onChange={e => updateRow(setRows, rows, i, { ...r, op: e.target.value as FilterOp })}
              className="bg-slate-900 border border-slate-800 rounded px-2 py-1.5"
            >
              {FILTER_OPS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            <input
              value={r.value === null ? '' : String(r.value)}
              onChange={e => updateRow(setRows, rows, i, { ...r, value: e.target.value })}
              placeholder={r.op === 'in' ? 'csv,values' : r.op === 'exists' ? 'true / false' : 'value'}
              className="bg-slate-900 border border-slate-800 rounded px-2 py-1.5 flex-1 min-w-[8rem] font-mono"
            />
            <button
              onClick={() => setRows(rows.filter((_, j) => j !== i))}
              className="tap-target text-slate-500 hover:text-rose-400"
              aria-label="remove filter"
            >
              <XIcon className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>

      {/* Schema-driven datalist for field autocomplete */}
      <datalist id={`schema-${name}`}>
        {(schema.data?.fields ?? []).map(f => (
          <option key={f.path} value={f.path}>{`${f.path}  (${f.type})`}</option>
        ))}
      </datalist>

      {/* Projection picker */}
      {schema.data && schema.data.fields.length > 0 && (
        <details className="bg-slate-900/40 border border-slate-800 rounded text-xs">
          <summary className="cursor-pointer px-3 py-2 text-slate-400">
            projection ({selectedFields.length || 'all'})
          </summary>
          <div className="p-3 grid grid-cols-2 sm:grid-cols-3 gap-1">
            {schema.data.fields.map(f => (
              <label key={f.path} className="flex items-center gap-1.5 text-slate-300 truncate">
                <input
                  type="checkbox"
                  checked={selectedFields.includes(f.path)}
                  onChange={e => {
                    if (e.target.checked) setSelectedFields([...selectedFields, f.path]);
                    else setSelectedFields(selectedFields.filter(p => p !== f.path));
                  }}
                />
                <span className="font-mono truncate">{f.path}</span>
                <span className="text-slate-600 text-[10px]">{f.type}</span>
              </label>
            ))}
          </div>
        </details>
      )}

      {/* Result */}
      {submitted && result.isLoading && <LoadingRow label="Running…" />}
      {submitted && result.isError   && <ErrorRow   label={`Query failed: ${(result.error as Error)?.message ?? 'unknown'}`} />}
      {result.data && (
        <>
          <div className="text-xs text-slate-500">
            {result.data.total_is_estimate ? '~' : ''}{result.data.total.toLocaleString()} total ·
            showing {result.data.docs.length} · {result.data.took_ms}ms
          </div>
          <div className="space-y-1">
            {result.data.docs.map((d, i) => (
              <DocRow key={String((d as any)._id ?? i)} doc={d} />
            ))}
            {result.data.docs.length === 0 && (
              <div className="text-sm text-slate-500 py-4 text-center">No matching docs.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function updateRow(setter: (r: FilterRow[]) => void, current: FilterRow[], i: number, next: FilterRow) {
  const out = current.slice();
  out[i] = next;
  setter(out);
}

// ─── Block-detection badge for the Scraped HTML modal ───────────────────────

function BlockSignalsBadge({ id }: { id: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['dbview', 'scraped-signals', id],
    queryFn:  () => getScrapedDocSignals(id),
  });
  if (isLoading) return null;
  if (!data)     return null;
  const s = data.signals;
  return <BlockSignalsRow signals={s} />;
}

function BlockSignalsRow({ signals }: { signals: BlockSignals }) {
  const verdict = signals.verdict;
  const palette = verdict === 'healthy'      ? 'bg-emerald-900/40 text-emerald-300 border-emerald-700'
                : verdict === 'partial_block' ? 'bg-amber-900/40   text-amber-300   border-amber-700'
                : verdict === 'hard_block'    ? 'bg-rose-900/40    text-rose-300    border-rose-700'
                :                               'bg-slate-800       text-slate-400   border-slate-700';
  const icon = verdict === 'healthy' ? '✓' : verdict === 'partial_block' ? '!' : verdict === 'hard_block' ? '⛔' : '?';
  return (
    <div className={cn('px-3 py-2 rounded border text-xs flex flex-wrap items-center gap-x-3 gap-y-1', palette)}>
      <span className="font-semibold">{icon} {verdict.replace('_', ' ')}</span>
      <span>registry={signals.registry_status}</span>
      <span>parsed={signals.parsed_listings}</span>
      {signals.html_bytes !== null && <span>html={Math.round(signals.html_bytes / 1024)}kB</span>}
      {signals.title_block_match && <span>title=&quot;{signals.title_block_match}&quot;</span>}
    </div>
  );
}

// ─── Anchor Config tab ──────────────────────────────────────────────────────
//
// Editor for `app_config.anchor_formula` (Tony's three-tier weighted blend).
// READ-ONLY view of the active config + form to patch it. Mutations write
// back via /v1/observe/dbview/anchor-config (PATCH) and invalidate the
// `dbview.anchor_config` query so the UI re-syncs from the server's
// post-write state (so "what we just saved" matches "what the next anchor
// lookup will read"). Validation lives server-side; this layer only does
// shape-level guards (weight inputs are 0–1 numerics, no non-numeric text).

function AnchorConfigTab() {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['dbview', 'anchor_config'],
    queryFn:  getAnchorConfig,
  });

  // Local form state mirrors the server's config; reset on server data load.
  const [mode, setMode] = useState<AnchorFormulaMode>('legacy');
  const [market, setMarket] = useState<number>(0.40);
  const [used,   setUsed]   = useState<number>(0.35);
  const [retail, setRetail] = useState<number>(0.25);
  const [iqrK,   setIqrK]   = useState<number>(1.5);
  const [minSales, setMinSales] = useState<number>(5);

  useEffect(() => {
    if (!data?.config) return;
    setMode(data.config.mode);
    setMarket(data.config.weights.market);
    setUsed(data.config.weights.used);
    setRetail(data.config.weights.retail);
    setIqrK(data.config.ebaySoldOutlierIqrK);
    setMinSales(data.config.minEbaySoldSales);
  }, [data?.config]);

  const weightSum = market + used + retail;
  const sumOk = Math.abs(weightSum - 1) <= 0.001;

  const saveMut = useMutation({
    mutationFn: (patch: Partial<AnchorFormulaConfig>) => patchAnchorConfig(patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dbview', 'anchor_config'] }),
  });

  const onSave = () => {
    saveMut.mutate({
      mode,
      weights: { market, used, retail },
      ebaySoldOutlierIqrK: iqrK,
      minEbaySoldSales: minSales,
    });
  };

  const onResetToDefaults = () => {
    if (!data?.defaults) return;
    setMode(data.defaults.mode);
    setMarket(data.defaults.weights.market);
    setUsed(data.defaults.weights.used);
    setRetail(data.defaults.weights.retail);
    setIqrK(data.defaults.ebaySoldOutlierIqrK);
    setMinSales(data.defaults.minEbaySoldSales);
  };

  if (isLoading) return <div className="text-sm text-slate-400">Loading anchor config…</div>;
  if (isError)   return <div className="text-sm text-red-400">Failed to load: {(error as any)?.message}</div>;

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="rounded border border-slate-800 bg-slate-900/40 p-4 space-y-2">
        <div className="text-xs uppercase tracking-wide text-slate-500">Anchor formula — Tony's three-tier weighted blend</div>
        <p className="text-sm text-slate-400 leading-relaxed">
          <strong>used floor</strong> (CeX + MusicMagpie) ·{' '}
          <strong>market</strong> (eBay sold ≥ <code className="text-slate-300">minEbaySoldSales</code>, IQR-stripped; falls back to live eBay listings) ·{' '}
          <strong>retail</strong> (Amazon + Argos + Currys median).
          Each tier is a single price; the final anchor is the weighted blend with weights renormalised over present tiers.
          Mode <code className="text-slate-300">legacy</code> uses the old median-of-medians formula.
        </p>
        {data?.config?.updatedAt && (
          <div className="text-xs text-slate-500">
            Last updated {new Date(data.config.updatedAt).toLocaleString()}
            {data.config.updatedBy ? <> by <code className="text-slate-300">tgId:{data.config.updatedBy}</code></> : ' (seeded)'}
          </div>
        )}
      </div>

      <div className="rounded border border-slate-800 bg-slate-900/40 p-4 space-y-4">
        <div className="space-y-2">
          <label className="text-xs uppercase tracking-wide text-slate-500">Mode</label>
          <div className="flex items-center gap-1 bg-slate-900 border border-slate-800 rounded-md p-0.5 w-max">
            {(['legacy', 'weighted'] as AnchorFormulaMode[]).map(m => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={cn(
                  'px-3 py-1 text-xs font-medium rounded transition',
                  mode === m ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white',
                )}
              >
                {m}
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-500">
            {mode === 'weighted'
              ? 'Active: anchor lookups use the three-tier blend below.'
              : 'Legacy median-of-medians is in effect; the weights below are read but unused.'}
          </p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <WeightField label="Market" hint="eBay sold (preferred)" value={market} onChange={setMarket} />
          <WeightField label="Used"   hint="CeX + MusicMagpie"     value={used}   onChange={setUsed} />
          <WeightField label="Retail" hint="Amazon / Argos / Currys" value={retail} onChange={setRetail} />
        </div>
        <div className={cn('text-xs', sumOk ? 'text-emerald-400' : 'text-red-400')}>
          weights sum: {weightSum.toFixed(3)} {sumOk ? '✓' : '— must equal 1.000'}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <NumericField
            label="eBay-sold IQR k"
            hint="Tukey constant. 1.5 = standard outlier rule."
            min={0.1} step={0.1}
            value={iqrK} onChange={setIqrK}
          />
          <NumericField
            label="Min eBay-sold sales"
            hint="Tier requires ≥ this many sales (Tony spec: 5)."
            min={1} step={1}
            value={minSales} onChange={setMinSales}
          />
        </div>

        <div className="flex items-center gap-2 pt-2 border-t border-slate-800">
          <button
            onClick={onSave}
            disabled={!sumOk || saveMut.isPending}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded font-medium transition',
              sumOk && !saveMut.isPending
                ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                : 'bg-slate-800 text-slate-500 cursor-not-allowed',
            )}
          >
            <Save className="w-3.5 h-3.5" />
            {saveMut.isPending ? 'Saving…' : 'Save'}
          </button>
          <button
            onClick={onResetToDefaults}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded font-medium text-slate-300 hover:text-white border border-slate-700 hover:border-slate-600 transition"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset to defaults
          </button>
          {saveMut.isError && (
            <span className="text-xs text-red-400">{(saveMut.error as any)?.message ?? 'save failed'}</span>
          )}
          {saveMut.isSuccess && !saveMut.isPending && (
            <span className="text-xs text-emerald-400">Saved.</span>
          )}
        </div>
      </div>
    </div>
  );
}

function WeightField({
  label, hint, value, onChange,
}: { label: string; hint: string; value: number; onChange: (v: number) => void }) {
  return (
    <label className="block space-y-1">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <input
        type="number" step={0.05} min={0} max={1}
        value={Number.isFinite(value) ? value : 0}
        onChange={(e) => {
          const n = Number(e.target.value);
          onChange(Number.isFinite(n) ? n : 0);
        }}
        className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-1.5 text-sm font-mono"
      />
      <div className="text-xs text-slate-500">{hint}</div>
    </label>
  );
}

function NumericField({
  label, hint, min, max, step, value, onChange,
}: { label: string; hint: string; min: number; max?: number; step: number; value: number; onChange: (v: number) => void }) {
  return (
    <label className="block space-y-1">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <input
        type="number" step={step} min={min} {...(max !== undefined ? { max } : {})}
        value={Number.isFinite(value) ? value : min}
        onChange={(e) => {
          const n = Number(e.target.value);
          onChange(Number.isFinite(n) ? n : min);
        }}
        className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-1.5 text-sm font-mono"
      />
      <div className="text-xs text-slate-500">{hint}</div>
    </label>
  );
}

// ─── Image-card renderer health (2026-05-18) ────────────────────────────────
//
// Resource-monitoring panel for the Reseller PNG card path
// (deal.image-card.ts). Shows the 24h totals at a glance — calls/hr,
// cache-hit rate, p95 cold-render latency, failure breakdown, and the
// last failure's error message. Refetches every 30s via the panel's
// owning ScoringRulesTab query (refetchInterval: 30_000).
import type { ImageCardMetrics, EmitLatencyMetrics, DeliveryLatencyMetrics } from '../api/dbview';

function ImageCardHealthPanel({
  snapshot, loading, error,
}: {
  snapshot: ImageCardMetrics | undefined;
  loading: boolean;
  error: string | null;
}) {
  return (
    <div className="rounded border border-slate-800 bg-slate-900/40 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wide text-slate-500">
          Image-card renderer · health · last 24h
        </div>
        <div className="text-xs text-slate-500">
          {loading ? 'refreshing…' : 'live · 30s refresh'}
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-400">Failed to load: {error}</div>
      )}

      {snapshot && (
        <>
          {/* Top row — headline numbers */}
          <div className="grid grid-cols-4 gap-3">
            <MetricStat label="Renders" value={snapshot.total.toLocaleString()}
                  sub={`${snapshot.perHour.toFixed(1)}/hr`} />
            <MetricStat label="Cache hit" value={`${Math.round(snapshot.cacheHitRate * 100)}%`}
                  sub={`${snapshot.byOutcome.cache_hit} hits · ${snapshot.byOutcome.rendered} cold`} />
            <MetricStat label="p95 cold" value={snapshot.duration.p95Ms != null
                    ? `${Math.round(snapshot.duration.p95Ms)} ms` : '—'}
                  sub={snapshot.duration.coldRenders > 0
                    ? `n=${snapshot.duration.coldRenders}` : 'no cold renders'} />
            <MetricStat label="Mean PNG"
                  value={snapshot.pngBytes.meanBytes != null
                    ? `${(snapshot.pngBytes.meanBytes / 1024).toFixed(0)} KB` : '—'}
                  sub={snapshot.pngBytes.coldRenders > 0
                    ? `n=${snapshot.pngBytes.coldRenders}` : 'no renders yet'} />
          </div>

          {/* Outcome breakdown — the failure modes worth alerting on */}
          <div className="grid grid-cols-4 gap-3">
            <OutcomeChip label="cache_hit"      n={snapshot.byOutcome.cache_hit}      tone="ok" />
            <OutcomeChip label="rendered"        n={snapshot.byOutcome.rendered}        tone="ok" />
            <OutcomeChip label="failed_no_font"  n={snapshot.byOutcome.failed_no_font}  tone="warn" />
            <OutcomeChip label="failed_render"   n={snapshot.byOutcome.failed_render}   tone="danger" />
          </div>

          {/* Latency percentiles + auxiliary stats */}
          <div className="text-xs text-slate-400 grid grid-cols-3 gap-3">
            <div>
              <span className="text-slate-500">p50/p99 cold render </span>
              <span className="text-slate-200">
                {snapshot.duration.p50Ms != null ? `${Math.round(snapshot.duration.p50Ms)}` : '—'}
                {' / '}
                {snapshot.duration.p99Ms != null ? `${Math.round(snapshot.duration.p99Ms)}` : '—'} ms
              </span>
            </div>
            <div>
              <span className="text-slate-500">image-fetch ok rate </span>
              <span className="text-slate-200">
                {snapshot.imageFetchOkRate != null
                  ? `${Math.round(snapshot.imageFetchOkRate * 100)}%`
                  : 'n/a'}
              </span>
            </div>
            <div>
              <span className="text-slate-500">mean cold render </span>
              <span className="text-slate-200">
                {snapshot.duration.meanMs != null
                  ? `${Math.round(snapshot.duration.meanMs)} ms` : '—'}
              </span>
            </div>
          </div>

          {snapshot.lastFailure && (
            <div className="text-xs text-red-300 border-t border-slate-800 pt-2">
              <span className="text-slate-500">last failure </span>
              {new Date(snapshot.lastFailure.ts).toLocaleString()}
              {': '}
              <code className="text-red-300">{snapshot.lastFailure.errorMessage}</code>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MetricStat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded bg-slate-950 border border-slate-800 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-lg font-mono text-slate-100 mt-0.5">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

// 2026-05-18 — Source-to-emit latency panel (Tony's "what's our scraper
// latency" ask). Reads (firstEmittedAt − firstSeenAt) over the last 24h
// and shows p50/p95/p99 overall plus a per-source breakdown so anomalies
// localise. Bounded above by scrape cadence — typical p50 ≈ one cycle
// (~90 min). Live · 30s refresh.
function EmitLatencyPanel({
  snapshot, loading, error,
}: {
  snapshot: EmitLatencyMetrics | undefined;
  loading: boolean;
  error: string | null;
}) {
  const fmt = (ms: number | null) => {
    if (ms == null) return '—';
    if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
    if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m`;
    return `${(ms / 3_600_000).toFixed(1)}h`;
  };

  return (
    <div className="rounded border border-slate-800 bg-slate-900/40 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wide text-slate-500">
          Source → emit latency · last 24h
        </div>
        <div className="text-xs text-slate-500">
          {loading ? 'refreshing…' : 'live · 30s refresh'}
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-400">Failed to load: {error}</div>
      )}

      {snapshot && (
        <>
          <div className="text-[11px] text-slate-500 -mt-1">
            Time between us first detecting a listing (firstSeenAt) and first publishing it
            to users (firstEmittedAt). Bounded above by scrape cadence — typical p50 should
            sit around one cycle (~90 min). NOT source-listing-time → emit (we don't
            capture the source's own listing timestamp).
          </div>

          <div className="grid grid-cols-4 gap-3">
            <MetricStat label="Emits (window)" value={snapshot.total.toLocaleString()}
                  sub={`${snapshot.windowHours}h window`} />
            <MetricStat label="p50" value={fmt(snapshot.overall.p50Ms)} />
            <MetricStat label="p95" value={fmt(snapshot.overall.p95Ms)} />
            <MetricStat label="p99" value={fmt(snapshot.overall.p99Ms)} />
          </div>

          {snapshot.bySource.length > 0 && (
            <div className="border-t border-slate-800 pt-2 space-y-1">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">
                by source
              </div>
              <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-x-3 gap-y-1 text-xs font-mono">
                <div className="text-slate-500">source</div>
                <div className="text-slate-500 text-right">n</div>
                <div className="text-slate-500 text-right">p50</div>
                <div className="text-slate-500 text-right">p95</div>
                <div className="text-slate-500 text-right">p99</div>
                {snapshot.bySource.map(row => (
                  <Fragment key={row.source}>
                    <div className="text-slate-300">{row.source}</div>
                    <div className="text-slate-400 text-right">{row.count}</div>
                    <div className="text-slate-200 text-right">{fmt(row.p50Ms)}</div>
                    <div className="text-slate-200 text-right">{fmt(row.p95Ms)}</div>
                    <div className="text-slate-200 text-right">{fmt(row.p99Ms)}</div>
                  </Fragment>
                ))}
              </div>
            </div>
          )}

          {snapshot.total === 0 && (
            <div className="text-xs text-slate-500 border-t border-slate-800 pt-2">
              No emits with a firstEmittedAt stamp in this window yet. The field is
              set the first time a deal clears DCS EMIT — historical deals won't
              have it until they re-emit.
            </div>
          )}
        </>
      )}
    </div>
  );
}

// 2026-06-07 — Delivery latency (emit→delivery, the USER-FACING leg the
// emit-latency panel above does NOT cover). Splits FRESH (deal first
// emitted in-window — the real latency) from RE-DELIVERY (old-but-live
// deal re-sent to a newly-eligible user — looks slow vs the weeks-old
// firstEmittedAt but was actually sent ~1s after re-match). Targets:
// free ≤5m, pro/reseller ≤1m. Live · 30s refresh.
function DeliveryLatencyPanel({
  snapshot, loading, error,
}: {
  snapshot: DeliveryLatencyMetrics | undefined;
  loading: boolean;
  error: string | null;
}) {
  const fmt = (ms: number | null) => {
    if (ms == null) return '—';
    if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
    if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m`;
    return `${(ms / 3_600_000).toFixed(1)}h`;
  };
  // RAG colour a tier's p50 against its target (free 5m, pro/reseller 1m).
  const rag = (tier: string, p50: number | null): string => {
    if (p50 == null || !snapshot) return 'text-slate-200';
    const target = tier === 'free' ? snapshot.targetFreeMs : snapshot.targetProMs;
    if (p50 <= target) return 'text-emerald-400';
    if (p50 <= target * 3) return 'text-amber-400';
    return 'text-red-400';
  };

  return (
    <div className="rounded border border-slate-800 bg-slate-900/40 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wide text-slate-500">
          Emit → delivery latency · last 24h
        </div>
        <div className="text-xs text-slate-500">
          {loading ? 'refreshing…' : 'live · 30s refresh'}
        </div>
      </div>

      {error && <div className="text-sm text-red-400">Failed to load: {error}</div>}

      {snapshot && (
        <>
          <div className="text-[11px] text-slate-500 -mt-1">
            Time from a deal being published (firstEmittedAt) to the user receiving the
            Telegram push (sent_at). <b>Fresh</b> = deal first-emitted this window (true
            latency). <b>Re-deliveries</b> = old-but-live deals re-sent to newly-eligible
            users — excluded from the percentiles below (they look slow vs a weeks-old
            firstEmittedAt but were sent within ~1s of being re-matched). Targets: free ≤5m,
            pro/reseller ≤1m.
          </div>

          <div className="grid grid-cols-4 gap-3">
            <MetricStat label="Fresh deliveries" value={snapshot.freshCount.toLocaleString()}
                  sub={`${snapshot.redeliveryCount.toLocaleString()} re-deliveries excluded`} />
            <MetricStat label="p50" value={fmt(snapshot.fresh.p50Ms)} />
            <MetricStat label="p90" value={fmt(snapshot.fresh.p90Ms)} />
            <MetricStat label="p95" value={fmt(snapshot.fresh.p95Ms)} />
          </div>

          {snapshot.freshCount > 0 && (
            <div className="border-t border-slate-800 pt-2 space-y-1">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">
                fresh latency by tier (p50 RAG vs target)
              </div>
              <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-x-3 gap-y-1 text-xs font-mono">
                <div className="text-slate-500">tier</div>
                <div className="text-slate-500 text-right">n</div>
                <div className="text-slate-500 text-right">p50</div>
                <div className="text-slate-500 text-right">p90</div>
                <div className="text-slate-500 text-right">p95</div>
                {snapshot.byTier.map(row => (
                  <Fragment key={row.tier}>
                    <div className="text-slate-300">{row.tier}</div>
                    <div className="text-slate-400 text-right">{row.count}</div>
                    <div className={`text-right ${rag(row.tier, row.p50Ms)}`}>{fmt(row.p50Ms)}</div>
                    <div className="text-slate-200 text-right">{fmt(row.p90Ms)}</div>
                    <div className="text-slate-200 text-right">{fmt(row.p95Ms)}</div>
                  </Fragment>
                ))}
              </div>
            </div>
          )}

          {snapshot.freshCount > 0 && (
            <div className="border-t border-slate-800 pt-2 space-y-1">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">
                fresh latency distribution
              </div>
              <div className="grid grid-cols-5 gap-2 text-xs font-mono text-center">
                {([
                  ['<1m', snapshot.freshBuckets.under1m],
                  ['1-5m', snapshot.freshBuckets.m1to5],
                  ['5-30m', snapshot.freshBuckets.m5to30],
                  ['30-60m', snapshot.freshBuckets.m30to60],
                  ['>1h', snapshot.freshBuckets.over1h],
                ] as Array<[string, number]>).map(([label, n]) => (
                  <div key={label} className="rounded bg-slate-800/40 py-1">
                    <div className="text-slate-200">{n}</div>
                    <div className="text-[9px] text-slate-500">{label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {snapshot.freshCount === 0 && (
            <div className="text-xs text-slate-500 border-t border-slate-800 pt-2">
              No fresh deliveries in this window (deals first-emitted inside the window and
              delivered). {snapshot.redeliveryCount > 0
                ? `${snapshot.redeliveryCount} re-deliveries of older deals were sent — those don't measure delivery latency.`
                : ''} {snapshot.noStampCount > 0
                ? `${snapshot.noStampCount} delivered deals had no firstEmittedAt stamp.`
                : ''}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function OutcomeChip({ label, n, tone }: { label: string; n: number; tone: 'ok' | 'warn' | 'danger' }) {
  const color = tone === 'ok'
    ? 'border-slate-700 text-slate-200'
    : tone === 'warn'
      ? 'border-amber-700/60 text-amber-300'
      : (n > 0 ? 'border-red-700/60 text-red-300' : 'border-slate-700 text-slate-400');
  return (
    <div className={`rounded border ${color} bg-slate-950 px-3 py-1.5 text-xs flex items-center justify-between`}>
      <code className="text-slate-400">{label}</code>
      <span className="font-mono">{n.toLocaleString()}</span>
    </div>
  );
}

// ─── Scoring Rules tab (2026-05-15) ─────────────────────────────────────────
//
// Two-section editor:
//
//   1. Editable section (top) — DB-driven knobs from app_config.quality_thresholds.
//      Currently just the phone structural-damage penalty + hard-drop flag.
//      Mutations write back via PATCH /scoring-rules and invalidate the query
//      so the read-only snapshot below also re-syncs with the new value.
//
//   2. Read-only snapshot (below) — every DCS rule currently in effect:
//      thresholds, trust tiers, quality penalties, condition sets, regex
//      patterns, category rules. Powered by the same endpoint so what's
//      shown is always what's live.

function ScoringRulesTab() {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['dbview', 'scoring_rules'],
    queryFn:  getScoringRules,
  });

  // 2026-05-18 — Image-card renderer health (Reseller PNG card).
  // Aggregates the last 24h of `image_card_renders`. Refetches every
  // 30s so the panel reads "live" without manual refresh. Independent
  // of the scoring-rules query above.
  const imgMetricsQ = useQuery({
    queryKey: ['dbview', 'image_card_metrics'],
    queryFn:  () => getImageCardMetrics(24),
    refetchInterval: 30_000,
  });

  // 2026-05-18 — Source-to-emit latency (Tony's scraper-latency ask).
  // Reads (firstEmittedAt − firstSeenAt) over the deals collection.
  const emitLatencyQ = useQuery({
    queryKey: ['dbview', 'emit_latency'],
    queryFn:  () => getEmitLatencyMetrics(24),
    refetchInterval: 30_000,
  });

  const deliveryLatencyQ = useQuery({
    queryKey: ['dbview', 'delivery_latency'],
    queryFn:  () => getDeliveryLatencyMetrics(24),
    refetchInterval: 30_000,
  });

  const [penalty, setPenalty] = useState<number>(3);
  const [hardDrop, setHardDrop] = useState<boolean>(false);
  // 2026-05-16 — category-wise gate editors. Render as KV rows the admin
  // can add/edit/delete. The dashboard sends the full map on save (no
  // per-key diff). Stored as object so empty values are preserved during
  // typing without resetting React state.
  const [catSaving,   setCatSaving]   = useState<Record<string, number>>({});
  const [catDiscount, setCatDiscount] = useState<Record<string, number>>({});
  const [catHardDrop, setCatHardDrop] = useState<Record<string, boolean>>({});
  // 2026-05-17 — DCS decision thresholds (dashboard-tunable). Defaults
  // 70/60. Server-side `validate()` enforces 0 ≤ hold < emit ≤ 100 and
  // returns 400 on inversion, which surfaces via the save-mutation error.
  const [dcsEmit, setDcsEmit] = useState<number>(70);
  const [dcsHold, setDcsHold] = useState<number>(60);
  // 2026-05-18 — PNG image-card threshold (Reseller-Pro). Default 90; 101
  // effectively disables the renderer (cheap kill-switch).
  const [dcsImageCard, setDcsImageCard] = useState<number>(90);
  // 2026-07-03 — Option B anchor-miss fallback: kill switch + sold-price guard.
  const [fallbackEnabled, setFallbackEnabled] = useState<boolean>(false);
  const [fallbackBelowSold, setFallbackBelowSold] = useState<number>(25);
  // 2026-10-23 — hold queue for disagreements only (ships dark).
  const [holdDisagreementsOnly, setHoldDisagreementsOnly] = useState<boolean>(false);

  useEffect(() => {
    if (!data?.editableConfig) return;
    setPenalty(data.editableConfig.phoneStructuralDamagePenalty);
    setHardDrop(data.editableConfig.phoneStructuralDamageHardDrop);
    setCatSaving({ ...(data.editableConfig.categoryMinSaving   ?? {}) });
    setCatDiscount({ ...(data.editableConfig.categoryMinDiscount ?? {}) });
    setCatHardDrop({ ...(data.editableConfig.categoryHardDrop    ?? {}) });
    if (data.editableConfig.dcsEmitThreshold != null) setDcsEmit(data.editableConfig.dcsEmitThreshold);
    if (data.editableConfig.dcsHoldThreshold != null) setDcsHold(data.editableConfig.dcsHoldThreshold);
    if (data.editableConfig.dcsImageCardThreshold != null) setDcsImageCard(data.editableConfig.dcsImageCardThreshold);
    if (typeof data.editableConfig.fallbackEmitEnabled === 'boolean') setFallbackEnabled(data.editableConfig.fallbackEmitEnabled);
    if (data.editableConfig.fallbackRequireBelowSoldPct != null) setFallbackBelowSold(data.editableConfig.fallbackRequireBelowSoldPct);
    if (typeof data.editableConfig.holdQueueDisagreementsOnly === 'boolean') setHoldDisagreementsOnly(data.editableConfig.holdQueueDisagreementsOnly);
  }, [data?.editableConfig]);

  const saveMut = useMutation({
    mutationFn: (patch: Partial<QualityConfigPayload>) => patchScoringRules(patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dbview', 'scoring_rules'] }),
  });

  const onSave = () => {
    saveMut.mutate({
      phoneStructuralDamagePenalty: penalty,
      phoneStructuralDamageHardDrop: hardDrop,
      categoryMinSaving:   catSaving,
      categoryMinDiscount: catDiscount,
      categoryHardDrop:    catHardDrop,
      dcsEmitThreshold: dcsEmit,
      dcsHoldThreshold: dcsHold,
      dcsImageCardThreshold: dcsImageCard,
      fallbackEmitEnabled: fallbackEnabled,
      fallbackRequireBelowSoldPct: fallbackBelowSold,
      holdQueueDisagreementsOnly: holdDisagreementsOnly,
    });
  };

  const onResetToDefaults = () => {
    if (!data?.defaults) return;
    setPenalty(data.defaults.phoneStructuralDamagePenalty);
    setHardDrop(data.defaults.phoneStructuralDamageHardDrop);
    setCatSaving({ ...(data.defaults.categoryMinSaving   ?? {}) });
    setCatDiscount({ ...(data.defaults.categoryMinDiscount ?? {}) });
    setCatHardDrop({ ...(data.defaults.categoryHardDrop    ?? {}) });
    if (data.defaults.dcsEmitThreshold != null) setDcsEmit(data.defaults.dcsEmitThreshold);
    if (data.defaults.dcsHoldThreshold != null) setDcsHold(data.defaults.dcsHoldThreshold);
    if (data.defaults.dcsImageCardThreshold != null) setDcsImageCard(data.defaults.dcsImageCardThreshold);
    if (typeof data.defaults.fallbackEmitEnabled === 'boolean') setFallbackEnabled(data.defaults.fallbackEmitEnabled);
    if (data.defaults.fallbackRequireBelowSoldPct != null) setFallbackBelowSold(data.defaults.fallbackRequireBelowSoldPct);
    if (typeof data.defaults.holdQueueDisagreementsOnly === 'boolean') setHoldDisagreementsOnly(data.defaults.holdQueueDisagreementsOnly);
  };

  if (isLoading) return <div className="text-sm text-slate-400">Loading scoring rules…</div>;
  if (isError)   return <div className="text-sm text-red-400">Failed to load: {(error as any)?.message}</div>;
  if (!data)     return null;

  const summary = data.rulesSummary;

  return (
    <div className="space-y-4 max-w-5xl">
      {/* Editable section */}
      <div className="rounded border border-slate-800 bg-slate-900/40 p-4 space-y-2">
        <div className="text-xs uppercase tracking-wide text-slate-500">DCS scoring rules — live config</div>
        <p className="text-sm text-slate-400 leading-relaxed">
          DB-driven knobs that admins can tune without a deploy. Reads + writes go through{' '}
          <code className="text-slate-300">app_config.quality_thresholds</code>. The full set of active rules is shown read-only below;
          changes to those require a deploy.
        </p>
        {data.editableConfig?.updatedAt && (
          <div className="text-xs text-slate-500">
            Last updated {new Date(data.editableConfig.updatedAt).toLocaleString()}
            {data.editableConfig.updatedBy
              ? <> by <code className="text-slate-300">tgId:{data.editableConfig.updatedBy}</code></>
              : ' (seeded)'}
          </div>
        )}
      </div>

      <div className="rounded border border-slate-800 bg-slate-900/40 p-4 space-y-4">
        {/* 2026-05-17 — DCS decision thresholds. Wired Sumit/Tony 2026-05-17
            chat ("lets go to 70" — lowered from 75 default to emit more
            borderline deals). Range 0–100; hold must be strictly less than
            emit (server-side validate enforces this and returns 400 on
            inversion). */}
        <div className="grid grid-cols-2 gap-4">
          <NumericField
            label="DCS → EMIT threshold"
            hint="DCS at-or-above this score is sent to users. Default 70 (was 75 pre-2026-05-17). Range 0–100."
            min={0} max={100} step={1}
            value={dcsEmit} onChange={setDcsEmit}
          />
          <NumericField
            label="DCS → HOLD threshold (lower bound)"
            hint="DCS in [hold, emit) goes to the review queue. Must be strictly less than EMIT. Default 60."
            min={0} max={100} step={1}
            value={dcsHold} onChange={setDcsHold}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <NumericField
            label="DCS → Image-card threshold (Reseller PNG)"
            hint="Reseller-Pro users get a server-rendered PNG card for deals at-or-above this DCS. Default 90. Set to 101 to disable the renderer entirely."
            min={0} max={101} step={1}
            value={dcsImageCard} onChange={setDcsImageCard}
          />
        </div>

        {/* Hold queue for disagreements only (2026-10-23, ships dark) */}
        <div className="rounded border border-sky-800/40 bg-sky-950/10 p-3 space-y-2">
          <div className="text-xs font-semibold text-sky-400">Hold queue — disagreements only</div>
          <div className="flex items-center gap-1 bg-slate-900 border border-slate-800 rounded-md p-0.5 w-max">
            {[false, true].map(v => (
              <button key={String(v)} onClick={() => setHoldDisagreementsOnly(v)}
                className={cn('px-3 py-1 text-xs font-medium rounded transition',
                  holdDisagreementsOnly === v ? (v ? 'bg-emerald-800 text-white' : 'bg-slate-800 text-white') : 'text-slate-400 hover:text-white')}>
                {v ? 'ON (disagreements only)' : 'OFF (today: hold below-threshold deals)'}
              </button>
            ))}
          </div>
          <div className="text-xs text-slate-500">
            ON: deals that only scored between the hold and emit thresholds are dropped, not held; a listing our
            title models did not agree on goes to the hold queue when any model names a live category. Holds for
            other reasons (no product, 48h cooldown, suspected auction, photo colour range / several products) stay.
          </div>
        </div>

        {/* Option B — anchor-miss scoring fallback (2026-07-03) */}
        <div className="rounded border border-amber-800/40 bg-amber-950/10 p-3 space-y-2">
          <div className="text-xs font-semibold text-amber-400">Option B — anchor-miss scoring fallback</div>
          <div className="flex flex-wrap items-end gap-4">
            <label className="block space-y-1">
              <div className="text-xs uppercase tracking-wide text-slate-500">Fallback emit</div>
              <div className="flex items-center gap-1 bg-slate-900 border border-slate-800 rounded-md p-0.5 w-max">
                {[false, true].map(v => (
                  <button key={String(v)} onClick={() => setFallbackEnabled(v)}
                    className={cn('px-3 py-1 text-xs font-medium rounded transition',
                      fallbackEnabled === v ? (v ? 'bg-emerald-800 text-white' : 'bg-slate-800 text-white') : 'text-slate-400 hover:text-white')}>
                    {v ? 'ON (releasing)' : 'OFF (dark)'}
                  </button>
                ))}
              </div>
            </label>
            <div className="w-64">
              <NumericField
                label="Require % below eBay sold"
                hint="Fallback deals that HAVE an eBay sold anchor must be at least this % below it — guards against the mv=5 price-conflict fake bargains (51% were at/above sold). 0 = spec-literal, no guard."
                min={0} max={100} step={1}
                value={fallbackBelowSold} onChange={setFallbackBelowSold}
              />
            </div>
          </div>
          <div className="text-xs text-slate-500">
            Releases HOLDs stuck purely by anchor-miss (market_validation_score = 5) at DCS 65–69 when price_score ≥ 35,
            condition is clean, and a modelKey exists. Ships <span className="text-slate-300">OFF</span>; flip ON only after
            sign-off, then watch fallback emit volume + rejection rate in Analyze.
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <NumericField
            label="Phone structural-damage penalty"
            hint="Quality-score deduction when title mentions cracked back/screen/glass etc. Default 3. Range 0–25."
            min={0} step={1}
            value={penalty} onChange={setPenalty}
          />
          <label className="block space-y-1">
            <div className="text-xs uppercase tracking-wide text-slate-500">Hard-drop on structural damage</div>
            <div className="flex items-center gap-1 bg-slate-900 border border-slate-800 rounded-md p-0.5 w-max">
              {[false, true].map(v => (
                <button
                  key={String(v)}
                  onClick={() => setHardDrop(v)}
                  className={cn(
                    'px-3 py-1 text-xs font-medium rounded transition',
                    hardDrop === v ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white',
                  )}
                >
                  {v ? 'on (suppress)' : 'off (penalty only)'}
                </button>
              ))}
            </div>
            <div className="text-xs text-slate-500">
              On: phones with structural damage in title hard-drop (FILTER.PHONE.STRUCTURAL_DAMAGE).
              Off (default): apply the penalty above + a card disclosure, keep deal in pool.
            </div>
          </label>
        </div>

        {/* Category gates (Sumit 2026-05-16) — add/remove per-category thresholds. */}
        <CategoryGateEditor
          title="Min absolute saving per category (£)"
          hint='Hard-drops deals whose (anchorPrice − price) is below this floor. Example: games=30 drops games saving under £30.'
          placeholder="games"
          unit="£"
          map={catSaving}
          onChange={setCatSaving}
        />
        <CategoryGateEditor
          title="Min discount % per category"
          hint='Hard-drops deals below this discount %. Example: games=40 drops games under 40% off. Range 0–100.'
          placeholder="games"
          unit="%"
          map={catDiscount}
          onChange={setCatDiscount}
        />
        <CategoryHardDropEditor
          map={catHardDrop}
          onChange={setCatHardDrop}
        />

        <div className="flex items-center gap-2 pt-2 border-t border-slate-800">
          <button
            onClick={onSave}
            disabled={saveMut.isPending}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded font-medium transition',
              !saveMut.isPending
                ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                : 'bg-slate-800 text-slate-500 cursor-not-allowed',
            )}
          >
            <Save className="w-3.5 h-3.5" />
            {saveMut.isPending ? 'Saving…' : 'Save'}
          </button>
          <button
            onClick={onResetToDefaults}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded font-medium text-slate-300 hover:text-white border border-slate-700 hover:border-slate-600 transition"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset to defaults
          </button>
          {saveMut.isError && (
            <span className="text-xs text-red-400">{(saveMut.error as any)?.message ?? 'save failed'}</span>
          )}
          {saveMut.isSuccess && !saveMut.isPending && (
            <span className="text-xs text-emerald-400">Saved.</span>
          )}
        </div>
      </div>

      {/* Read-only snapshot */}
      {/* 2026-05-18 — Image-card renderer health panel. Lives above the
          read-only snapshot because it's a LIVE signal (Sumit's "monitor
          resource usage" ask), not a config readout. Refetches every 30s. */}
      <ImageCardHealthPanel
        snapshot={imgMetricsQ.data?.snapshot}
        loading={imgMetricsQ.isLoading}
        error={imgMetricsQ.isError ? (imgMetricsQ.error as any)?.message : null}
      />

      <EmitLatencyPanel
        snapshot={emitLatencyQ.data?.snapshot}
        loading={emitLatencyQ.isLoading}
        error={emitLatencyQ.isError ? (emitLatencyQ.error as any)?.message : null}
      />

      <DeliveryLatencyPanel
        snapshot={deliveryLatencyQ.data?.snapshot}
        loading={deliveryLatencyQ.isLoading}
        error={deliveryLatencyQ.isError ? (deliveryLatencyQ.error as any)?.message : null}
      />

      <div className="rounded border border-slate-800 bg-slate-900/40 p-4 space-y-4">
        <div className="text-xs uppercase tracking-wide text-slate-500">
          All current rules — read-only snapshot
        </div>

        <RulesSection title="DCS thresholds">
          <KV k="DCS → EMIT"          v={`≥ ${summary.dcsThresholds.emit}`} />
          <KV k="DCS → HOLD"          v={`${summary.dcsThresholds.hold} – ${summary.dcsThresholds.emit - 1}`} />
          <KV k="DCS → Image card (Reseller)" v={summary.dcsThresholds.imageCard >= 101 ? 'disabled' : `≥ ${summary.dcsThresholds.imageCard}`} />
          <KV k="Price score min"     v={summary.dcsThresholds.priceScoreMin} />
          <KV k="Max quality score"   v={summary.dcsThresholds.maxQualityScore} />
          <KV k="Min quality score (hard-DROP floor)" v={summary.dcsThresholds.minQualityScore} />
        </RulesSection>

        <RulesSection title="Trust tiers">
          <TrustTierRow label="full (CeX, Back Market, Amazon-direct)"  members={summary.trustTiers.full} />
          <TrustTierRow label="retail (Argos, Currys, John Lewis)"      members={summary.trustTiers.retail} />
          <TrustTierRow label="graded (MusicMagpie)"                    members={summary.trustTiers.graded} />
          <TrustTierRow label="none (examples — anything not above)"   members={summary.trustTiers.noneExamples} />
        </RulesSection>

        <RulesSection title="Quality penalties">
          <div className="text-xs text-slate-500 mb-1">Apply on top of MAX_QUALITY_SCORE (25). Below the min floor (12) → hard DROP.</div>
          <div className="space-y-1">
            {summary.qualityPenalties.map((p, i) => (
              <div key={i} className="flex items-baseline gap-2 text-xs">
                <code className={cn(
                  'font-mono px-1.5 py-0.5 rounded shrink-0',
                  p.magnitude <= -20 ? 'bg-red-900/40 text-red-300' :
                  p.magnitude <= -5  ? 'bg-amber-900/40 text-amber-300' :
                                       'bg-slate-800 text-slate-300',
                )}>
                  {p.magnitude > 0 ? '+' : ''}{p.magnitude}
                </code>
                <span className="text-slate-300">{p.name}</span>
                <span className="text-slate-500 italic uppercase text-[10px]">{txt(p.category)}</span>
                {p.note && <span className="text-slate-500">— {p.note}</span>}
              </div>
            ))}
          </div>
        </RulesSection>

        <RulesSection title="Condition sets">
          <KV k="good (passes)"      v={summary.conditionSets.good.join(', ')} mono />
          <KV k="fair (-8 penalty)"  v={summary.conditionSets.fair.join(', ')} mono />
          <KV k="bad (hard fail)"    v={summary.conditionSets.bad.join(', ')}  mono />
        </RulesSection>

        <RulesSection title="Regex patterns">
          <div className="space-y-2">
            {summary.regexPatterns.map((r, i) => (
              <div key={i} className="space-y-0.5">
                <div className="flex items-baseline gap-2">
                  <code className="text-xs font-mono text-emerald-300">{r.name}</code>
                  <span className="text-xs text-slate-500">— {r.description}</span>
                </div>
                <code className="block text-[11px] font-mono text-slate-400 bg-slate-950 border border-slate-800 rounded px-2 py-1 break-all">
                  {r.pattern}
                </code>
              </div>
            ))}
          </div>
        </RulesSection>

        <RulesSection title="Category-specific rules">
          <table className="w-full text-xs">
            <thead className="text-slate-500">
              <tr>
                <th className="text-left py-1 pr-2 font-medium uppercase tracking-wide">Category</th>
                <th className="text-left py-1 pr-2 font-medium uppercase tracking-wide">Rule</th>
                <th className="text-left py-1 font-medium uppercase tracking-wide">Value</th>
              </tr>
            </thead>
            <tbody className="text-slate-300">
              {summary.categoryRules.map((c, i) => (
                <tr key={i} className="border-t border-slate-800/50">
                  <td className="py-1 pr-2"><code className="text-slate-400">{txt(c.category)}</code></td>
                  <td className="py-1 pr-2">{c.rule}</td>
                  <td className="py-1 font-mono text-emerald-300">{c.value ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </RulesSection>
      </div>
    </div>
  );
}

function RulesSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5 pb-3 border-b border-slate-800/50 last:border-0 last:pb-0">
      <div className="text-xs uppercase tracking-wide text-slate-400 font-medium">{title}</div>
      {children}
    </div>
  );
}

function KV({ k, v, mono = false }: { k: string; v: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-baseline gap-2 text-xs">
      <span className="text-slate-500 shrink-0">{k}:</span>
      <span className={cn('text-slate-200', mono && 'font-mono text-[11px]')}>{v}</span>
    </div>
  );
}

function TrustTierRow({ label, members }: { label: string; members: string[] }) {
  return (
    <div className="flex items-baseline gap-2 text-xs">
      <span className="text-slate-500 shrink-0">{label}:</span>
      <div className="flex flex-wrap gap-1">
        {members.map((m, i) => (
          <code key={i} className="text-[11px] font-mono bg-slate-800 text-slate-300 px-1.5 py-0.5 rounded">
            {m}
          </code>
        ))}
      </div>
    </div>
  );
}

// ─── Category gate editors (Sumit 2026-05-16) ──────────────────────────────
//
// Two flavours:
//   CategoryGateEditor    — numeric value per category (min saving £, min discount %)
//   CategoryHardDropEditor — boolean per category (hard-drop on/off)
//
// Both render the current state as KV rows + an "add" footer. The map is
// owned by the parent so save batches all three maps in one PATCH call.

function CategoryGateEditor({
  title, hint, placeholder, unit, map, onChange,
}: {
  title: string;
  hint: string;
  placeholder: string;
  unit: string;
  map: Record<string, number>;
  onChange: (next: Record<string, number>) => void;
}) {
  const [newKey, setNewKey] = useState('');
  const [newVal, setNewVal] = useState<string>('');

  const updateValue = (k: string, raw: string) => {
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    onChange({ ...map, [k]: n });
  };
  const removeKey = (k: string) => {
    const { [k]: _, ...rest } = map;
    onChange(rest);
  };
  const addEntry = () => {
    const k = newKey.trim().toLowerCase();
    const n = Number(newVal);
    if (!k || !Number.isFinite(n)) return;
    onChange({ ...map, [k]: n });
    setNewKey('');
    setNewVal('');
  };

  const entries = Object.entries(map).sort(([a], [b]) => a.localeCompare(b));

  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wide text-slate-500">{title}</div>
      <div className="text-xs text-slate-500">{hint}</div>
      <div className="space-y-1">
        {entries.length === 0 && (
          <div className="text-xs text-slate-600 italic">No category gates set.</div>
        )}
        {entries.map(([k, v]) => (
          <div key={k} className="flex items-center gap-2 text-xs">
            <code className="font-mono text-slate-300 w-32 shrink-0">{k}</code>
            <input
              type="number"
              step={1} min={0}
              value={v}
              onChange={(e) => updateValue(k, e.target.value)}
              className="bg-slate-950 border border-slate-800 rounded px-2 py-1 text-sm font-mono w-28"
            />
            <span className="text-slate-500">{unit}</span>
            <button
              onClick={() => removeKey(k)}
              className="text-xs text-slate-500 hover:text-red-400 transition px-1.5 py-0.5"
              title="Remove this category gate"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
      {/* Add row */}
      <div className="flex items-center gap-2 text-xs pt-1">
        <input
          type="text"
          placeholder={placeholder}
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          className="bg-slate-950 border border-slate-800 rounded px-2 py-1 text-sm font-mono w-32"
        />
        <input
          type="number"
          step={1} min={0}
          placeholder="value"
          value={newVal}
          onChange={(e) => setNewVal(e.target.value)}
          className="bg-slate-950 border border-slate-800 rounded px-2 py-1 text-sm font-mono w-28"
        />
        <span className="text-slate-500">{unit}</span>
        <button
          onClick={addEntry}
          disabled={!newKey.trim() || !newVal.trim()}
          className={cn(
            'text-xs px-2 py-1 rounded font-medium transition',
            newKey.trim() && newVal.trim()
              ? 'text-emerald-400 hover:text-emerald-300 border border-emerald-700 hover:border-emerald-500'
              : 'text-slate-600 border border-slate-800 cursor-not-allowed',
          )}
        >
          + Add
        </button>
      </div>
    </div>
  );
}

function CategoryHardDropEditor({
  map, onChange,
}: {
  map: Record<string, boolean>;
  onChange: (next: Record<string, boolean>) => void;
}) {
  const [newKey, setNewKey] = useState('');

  const toggle = (k: string) => onChange({ ...map, [k]: !map[k] });
  const removeKey = (k: string) => {
    const { [k]: _, ...rest } = map;
    onChange(rest);
  };
  const addEntry = () => {
    const k = newKey.trim().toLowerCase();
    if (!k) return;
    onChange({ ...map, [k]: true });
    setNewKey('');
  };

  const entries = Object.entries(map).sort(([a], [b]) => a.localeCompare(b));

  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wide text-slate-500">Hard-drop categories</div>
      <div className="text-xs text-slate-500">
        Categories listed here are always dropped at the scoring layer with
        <code className="text-slate-400 px-1">FILTER.&lt;CAT&gt;.CATEGORY_HARD_DROP</code>.
        Defence in depth alongside filterDealsByPrefs for users with no category preferences set.
      </div>
      <div className="space-y-1">
        {entries.length === 0 && (
          <div className="text-xs text-slate-600 italic">No hard-drop categories set.</div>
        )}
        {entries.map(([k, on]) => (
          <div key={k} className="flex items-center gap-2 text-xs">
            <code className="font-mono text-slate-300 w-32 shrink-0">{k}</code>
            <button
              onClick={() => toggle(k)}
              className={cn(
                'px-2 py-1 text-xs font-medium rounded transition',
                on ? 'bg-red-900/40 text-red-300 border border-red-800/50'
                   : 'bg-slate-800 text-slate-400 border border-slate-700',
              )}
            >
              {on ? 'HARD DROP' : 'off'}
            </button>
            <button
              onClick={() => removeKey(k)}
              className="text-xs text-slate-500 hover:text-red-400 transition px-1.5 py-0.5"
              title="Remove this category"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 text-xs pt-1">
        <input
          type="text"
          placeholder="accessories"
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          className="bg-slate-950 border border-slate-800 rounded px-2 py-1 text-sm font-mono w-32"
        />
        <button
          onClick={addEntry}
          disabled={!newKey.trim()}
          className={cn(
            'text-xs px-2 py-1 rounded font-medium transition',
            newKey.trim()
              ? 'text-emerald-400 hover:text-emerald-300 border border-emerald-700 hover:border-emerald-500'
              : 'text-slate-600 border border-slate-800 cursor-not-allowed',
          )}
        >
          + Add (hard-drop on)
        </button>
      </div>
    </div>
  );
}
